import React, { useState, useEffect, useRef } from 'react';
import { db, appId } from './firebase-config';
import { collection, addDoc, updateDoc, doc, orderBy, onSnapshot, query, getDocs, where, limit, getDoc } from 'firebase/firestore';
import { Icon } from './StockControl'; // Importa el Icon desde el archivo principal
import {
    resolveStockProductsCollectionPath,
    resolveStockControlsCollectionPath,
    resolveStockControllersCollectionPath,
    resolveStockMovementsCollectionPath,
    resolveStockTasksCollectionPath,
    resolveStockPurchaseDocPath,
    resolveStockPurchaseLinesCollectionPath,
    resolveRouteDocPath,
    legacyStockProductControlsCollectionPath,
    legacyStockTasksCollectionPath,
    legacyStockProductsCollectionPath
} from './core/company/companyPaths';
import { getPurchaseLineProgress } from './modules/stock/stockFlowUtils';

const buildProductIdentityParts = (product = {}) =>
    [
        String(product?.category || '').trim(),
        String(product?.model || '').trim(),
        String(product?.brand || '').trim(),
        String(product?.featureLabel || '').trim(),
    ].filter(Boolean);

const buildProductIdentityLabel = (product = {}) => {
    const parts = buildProductIdentityParts(product);
    return parts.length ? parts.join(' · ') : 'Sin identidad ampliada';
};

const getControlDateLabel = (control = {}) => {
    const value = control.endTime?.toDate ? control.endTime.toDate() : control.createdAt?.toDate ? control.createdAt.toDate() : null;
    return value ? value.toLocaleString('es-AR') : 'sin fecha';
};

const getControlHeadline = (control = {}) => {
    if (String(control?.type || '').trim() === 'stock_adjustment') {
        return `Ajuste del ${getControlDateLabel(control)}`;
    }
    return `Control del ${getControlDateLabel(control)}`;
};

const getControlTypeLabel = (control = {}) => {
    if (String(control?.type || '').trim() === 'stock_adjustment') {
        return 'Ajuste de stock por diferencia';
    }
    return control?.type === 'manual' ? 'Carga manual' : 'Carga desde archivo';
};

const getControlResponsibleName = (control = {}) =>
    String(
        control?.adjustedByName ||
        control?.controllerDisplayNameSnapshot ||
        [control?.controllerNameSnapshot, control?.controllerLastNameSnapshot].filter(Boolean).join(' ') ||
        ''
    ).trim() || 'Sin responsable visible';

const ControlResponsibleAvatar = ({ photoURL = '', displayName = 'Controlador' }) => {
    const initials = String(displayName || 'Controlador')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || 'CT';

    if (String(photoURL || '').trim()) {
        return (
            <div className="flex h-11 w-11 shrink-0 overflow-hidden rounded-2xl border border-cyan-200 bg-cyan-50 text-cyan-700">
                <img
                    src={String(photoURL).trim()}
                    alt={displayName}
                    className="h-full w-full object-cover"
                />
            </div>
        );
    }

    return (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-cyan-200 bg-cyan-50 text-cyan-700">
            <span className="text-xs font-black tracking-[0.18em]">{initials}</span>
        </div>
    );
};

// --- Componente Interno para mostrar los detalles del control ---
const ControlDetailView = ({ control, showMessage }) => {
    const [historyItems, setHistoryItems] = useState([]);
    useEffect(() => {
        if (control?.countDetails) {
            try { setHistoryItems(JSON.parse(control.countDetails)); } 
            catch (error) { console.error("Error al parsear el historial:", error); setHistoryItems([]); }
        }
    }, [control]);

    const responsibleName = getControlResponsibleName(control);
    const responsiblePhotoURL = String(
        control?.resolvedControllerPhotoURL ||
        control?.controllerPhotoURLSnapshot ||
        ''
    ).trim();

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write('<html><head><title>Reporte de Control de Stock</title>');
        printWindow.document.write('<style>body{font-family:sans-serif} table{width:100%;border-collapse:collapse} th,td{border:1px solid #ddd;padding:8px} th{background-color:#f2f2f2}</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(`<h2>Reporte del ${control.endTime?.toDate().toLocaleString('es-AR')}</h2>`);
        printWindow.document.write(document.getElementById(`report-table-${control.id}`).outerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    const handleDownloadCSV = () => {
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "Producto,Stock Sistema,Stock Fisico,Diferencia,Observacion\r\n";
        historyItems.forEach(item => {
            const row = [
                `"${item.name.replace(/"/g, '""')}"`, 
                item.stockSystem, 
                item.stockPhysical, 
                item.difference, 
                `"${(item.observation || '').replace(/"/g, '""')}"`
            ].join(",");
            csvContent += row + "\r\n";
        });
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `reporte_stock_${control.endTime?.toDate().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showMessage("Archivo CSV descargado con éxito.", "success");
    };

    const handleShareByEmail = () => {
        try {
            const subject = `STOCK - ${control.productName || 'Reporte'}`;
            let htmlBody = `<p>Resumen del control de stock realizado el ${control.endTime?.toDate().toLocaleString('es-AR')}:</p>`;
            htmlBody += document.getElementById(`report-table-${control.id}`).outerHTML;
            let plainTextBody = `Resumen del control de stock realizado el ${control.endTime?.toDate().toLocaleString('es-AR')}:\n\n`;
            plainTextBody += "Producto | Stock Sistema | Stock Físico | Diferencia | Observación\n";
            plainTextBody += "-------------------------------------------------------------------\n";
            historyItems.forEach(item => {
                plainTextBody += `${item.name} | ${item.stockSystem} | ${item.stockPhysical} | ${item.difference > 0 ? '+' : ''}${item.difference} | ${item.observation || '-'}\n`;
            });

            const listener = (e) => {
                e.clipboardData.setData('text/html', htmlBody);
                e.clipboardData.setData('text/plain', plainTextBody);
                e.preventDefault();
            };
            document.addEventListener('copy', listener);
            const successful = document.execCommand('copy');
            document.removeEventListener('copy', listener);

            if (successful) {
                showMessage("Exportación exitosa. Copiado en portapapeles para pegar en tu mail.", "success");
                const body = `Hola,\n\nPega aquí el reporte del inventario (Ctrl+V).\n\nSaludos.`;
                const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                window.open(mailtoLink, '_blank');
            } else {
                showMessage("Error al copiar. Por favor, intenta copiar el reporte manualmente.", "error");
            }
        } catch (error) {
            console.error("Error al copiar o abrir el correo:", error);
            showMessage("No se pudo copiar la tabla.", "error");
        }
    };

    return (
        <div className="bg-slate-50 p-4 mt-2 rounded-lg border">
            <div className="mb-4 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-700">Responsable del control</p>
                <div className="mt-2 flex items-center gap-3">
                    <ControlResponsibleAvatar photoURL={responsiblePhotoURL} displayName={responsibleName} />
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{responsibleName}</p>
                        <p className="mt-1 text-xs text-slate-500">
                            {control?.sheetTitleSnapshot ? `Planilla ${control.sheetTitleSnapshot}` : 'Control físico registrado'}
                        </p>
                    </div>
                </div>
            </div>
            {String(control?.type || '').trim() === 'stock_adjustment' ? (
                <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">Ajuste asentado</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{control.adjustmentReasonSnapshot || 'Sin motivo visible'}</p>
                    {control.adjustmentNoteSnapshot ? <p className="mt-1 text-sm text-slate-600">{control.adjustmentNoteSnapshot}</p> : null}
                    <p className="mt-1 text-xs text-slate-500">
                        Responsable: {responsibleName}
                    </p>
                </div>
            ) : null}
            <div className="overflow-x-auto flex-grow">
                <table id={`report-table-${control.id}`} className="w-full text-sm text-left text-stone-500 border-collapse">
                    <thead className="text-xs text-stone-700 uppercase bg-stone-200 sticky top-0">
                        <tr>
                            <th scope="col" className="px-4 py-3 border-r border-stone-300">Producto</th>
                            <th scope="col" className="px-4 py-3 text-center border-r border-stone-300">Stock Sistema</th>
                            <th scope="col" className="px-4 py-3 text-center border-r border-stone-300">Stock Físico</th>
                            <th scope="col" className="px-4 py-3 text-center border-r border-stone-300">Diferencia</th>
                            <th scope="col" className="px-4 py-3">Observación</th>
                        </tr>
                    </thead>
                    <tbody>
                        {historyItems.map((item) => (
                            <tr key={item.id} className={`border-b ${item.difference !== 0 ? 'bg-yellow-50' : 'bg-white'}`}>
                                <th scope="row" className="px-4 py-4 font-medium text-stone-900 whitespace-normal break-words border-r border-stone-200">{item.name}</th>
                                <td className="px-4 py-4 text-center border-r border-stone-200">{item.stockSystem}</td>
                                <td className="px-4 py-4 text-center border-r border-stone-200">{item.stockPhysical}</td>
                                <td className={`px-4 py-4 text-center font-bold border-r border-stone-200 ${item.difference > 0 ? 'text-green-600' : item.difference < 0 ? 'text-red-600' : 'text-stone-700'}`}>{item.difference > 0 ? `+${item.difference}` : item.difference}</td>
                                <td className="px-4 py-4 text-xs whitespace-normal break-words">{item.observation}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-end items-center pt-4 border-t mt-4 gap-2">
                 <button onClick={handlePrint} className="flex items-center gap-2 bg-stone-200 text-stone-800 py-2 px-3 rounded-md hover:bg-stone-300 text-xs"><Icon path="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a8.986 8.986 0 014.586 0M12 17.25c-3.72 0-7.22-1.22-10.13-3.429C1.366 11.31 1 9.626 1 8.25 1 6.874 1.366 5.19 1.87 3.821A21.754 21.754 0 0112 3c2.28 0 4.47.34 6.61.956A21.754 21.754 0 0123 8.25c0 1.376-.366 3.059-.87 4.429-2.91 2.209-6.41 3.429-10.13 3.429z" className="w-4 h-4"/> Imprimir</button>
                 <button onClick={handleDownloadCSV} className="flex items-center gap-2 bg-stone-200 text-stone-800 py-2 px-3 rounded-md hover:bg-stone-300 text-xs"><Icon path="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" className="w-4 h-4"/> Descargar</button>
                 <button onClick={handleShareByEmail} className="flex items-center gap-2 bg-stone-200 text-stone-800 py-2 px-3 rounded-md hover:bg-stone-300 text-xs"><Icon path="M21.75 6.75v10.5a2.25 2.25 0 01-2.25-2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" className="w-4 h-4"/> Exportar para Mail</button>
            </div>
        </div>
    );
};

// ... (El resto de los modales no necesitan cambios)
export const StockCountModal = ({ product, show, onClose, onComplete, showMessage, taskToUpdate = null, companyId = 'default' }) => {
    const resolveSystemStockForProduct = (targetProduct = {}) => {
        const buckets = targetProduct?.stockBuckets || {};
        const physicalQty = Number(buckets?.physicalQty || 0);
        const pendingOutboundQty = Number(buckets?.pendingOutboundQty || 0);
        const repairQty = Number(buckets?.repairQty || 0);
        const damagedQty = Number(buckets?.damagedQty || 0);
        const returnedQty = Number(buckets?.returnedQty || 0);
        const reservedAdjustments = Math.max(0, pendingOutboundQty) + Math.max(0, repairQty) + Math.max(0, damagedQty) + Math.max(0, returnedQty);
        return Math.max(0, physicalQty - reservedAdjustments);
    };

    const buildSeededItem = (targetProduct = {}) => {
        const stockSystem = resolveSystemStockForProduct(targetProduct);
        return {
            id: targetProduct.id || `${Date.now()}-product`,
            name: `${targetProduct.name || 'Producto'}${buildProductIdentityLabel(targetProduct) !== 'Sin identidad ampliada' ? ` · ${buildProductIdentityLabel(targetProduct)}` : ''}`,
            stockSystem,
            stockPhysical: '',
            difference: 0 - stockSystem,
            observation: '',
        };
    };

    const [items, setItems] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [mode, setMode] = useState('initial');
    const [manualItemName, setManualItemName] = useState('');
    const [manualItemQty, setManualItemQty] = useState('');
    const [manualItemSystemQty, setManualItemSystemQty] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (show) {
            setStartTime(new Date());
            if (product) {
                setItems([buildSeededItem(product)]);
                setMode('manual');
                setManualItemName(product.name || '');
                setManualItemSystemQty(String(resolveSystemStockForProduct(product)));
                setManualItemQty('');
            }
        } else {
            setItems([]);
            setStartTime(null);
            setMode('initial');
            setManualItemName('');
            setManualItemQty('');
            setManualItemSystemQty('');
        }
    }, [show, product]);

    const handleFileParse = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setIsProcessing(true);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                let text = e.target.result;
                if (text.charCodeAt(0) === 0xFEFF) {
                    text = text.slice(1);
                }
                const allLines = text.split(/\r\n|\n/).filter(line => line.trim() !== '');
                if (allLines.length < 1) throw new Error("El archivo CSV parece estar vacío.");
                const headerLine = allLines[0];
                const delimiter = headerLine.includes(';') ? ';' : ',';
                const parsedItems = [];
                const failedLines = [];
                allLines.forEach((line, index) => {
                    try {
                        if (index === 0 && isNaN(parseInt(line.split(delimiter)[1], 10))) return;
                        const columns = line.split(delimiter).map(c => c.trim().replace(/"/g, ''));
                        if (columns.length < 2) throw new Error("Línea con formato incorrecto.");
                        const name = columns[0];
                        const theoreticalStock = parseInt(columns[1], 10);
                        if (!name || isNaN(theoreticalStock)) throw new Error(`El valor del stock no es un número válido.`);
                        parsedItems.push({ 
                            id: `${Date.now()}-${index}`, 
                            name, 
                            stockSystem: theoreticalStock, 
                            stockPhysical: '', 
                            difference: -theoreticalStock, 
                            observation: '' 
                        });
                    } catch (lineError) {
                        failedLines.push({ lineNumber: index + 1, line, error: lineError.message });
                    }
                });
                if (parsedItems.length === 0) throw new Error("No se pudieron procesar productos. Asegúrate de que el archivo tenga al menos dos columnas (Producto, Stock) y esté guardado como CSV.");
                setItems(parsedItems);
                setMode('file');
                showMessage(`Archivo procesado: ${parsedItems.length} productos cargados.`, "success");
                if (failedLines.length > 0) {
                    console.warn("Algunas líneas no se pudieron procesar:", failedLines);
                    showMessage(`${failedLines.length} líneas del archivo no se pudieron leer y fueron ignoradas.`, "warning", 6000);
                }
            } catch (error) {
                showMessage(error.message, "error", 6000);
                setItems([]);
            } finally {
                setIsProcessing(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        };
        reader.onerror = () => { setIsProcessing(false); showMessage("Error al leer el archivo.", "error"); };
        reader.readAsText(file, 'ISO-8859-1');
    };

    const handleItemChange = (id, field, value) => {
        setItems(items.map(item => {
            if (item.id === id) {
                const updatedItem = { ...item, [field]: value };
                if (field === 'stockPhysical') {
                    const physical = parseInt(value, 10) || 0;
                    updatedItem.difference = physical - (item.stockSystem || 0);
                }
                return updatedItem;
            }
            return item;
        }));
    };
    
    const handleAddManualItem = () => {
        if (!manualItemName.trim() || !manualItemQty.trim()) {
            showMessage("Debe ingresar nombre y cantidad física.", "warning");
            return;
        }
        const systemQty = parseInt(manualItemSystemQty, 10) || 0;
        const physicalQty = parseInt(manualItemQty, 10) || 0;
        const newItem = {
            id: `${Date.now()}-${manualItemName}`,
            name: manualItemName.trim(),
            stockSystem: systemQty,
            stockPhysical: physicalQty,
            difference: physicalQty - systemQty,
            observation: ''
        };
        setItems([...items, newItem]);
        setManualItemName('');
        setManualItemQty('');
        setManualItemSystemQty('');
    };

    const handleSaveAndComplete = async () => {
        if (items.length === 0 || (mode === 'file' && items.every(item => item.stockPhysical === ''))) {
            showMessage("Por favor, complete el conteo de al menos un producto.", "warning");
            return;
        }
        setIsProcessing(true);
        try {
            const historyRef = collection(
                db,
                product?._source === 'legacy'
                    ? legacyStockProductControlsCollectionPath(appId, product.id)
                    : resolveStockControlsCollectionPath(companyId)
            );
            await addDoc(historyRef, {
                startTime,
                endTime: new Date(),
                countDetails: JSON.stringify(items),
                type: mode,
                productId: product.id,
                productName: product.name,
                companyId,
                controllerId: taskToUpdate?.assignedControllerId || null,
                controllerNameSnapshot: taskToUpdate?.assignedControllerNameSnapshot || null,
                controllerLastNameSnapshot: taskToUpdate?.assignedControllerLastNameSnapshot || null,
                controllerDisplayNameSnapshot: taskToUpdate?.assignedControllerDisplayNameSnapshot || null,
                controllerPhotoURLSnapshot: taskToUpdate?.assignedControllerPhotoURLSnapshot || null,
                sheetId: taskToUpdate?.sheetId || null,
                sheetTitleSnapshot: taskToUpdate?.sheetTitleSnapshot || null,
                completedByUserId: taskToUpdate?.assignedControllerId || null,
            });
            if (taskToUpdate) {
                const taskPath = taskToUpdate?._source === 'legacy'
                    ? legacyStockTasksCollectionPath(appId)
                    : resolveStockTasksCollectionPath(companyId);
                await updateDoc(doc(db, taskPath, taskToUpdate.id), { status: 'completed' });
            }
            onComplete(product.id);
            showMessage("Control guardado en el historial con éxito.", "success");
        } catch (error) {
            showMessage(`Error al guardar el control: ${error.message}`, "error");
        } finally {
            setIsProcessing(false);
        }
    };
    
    const renderInitial = () => (
        <div className="flex flex-col items-center justify-center flex-grow">
            <h4 className="text-lg font-semibold text-stone-700 mb-6">¿Cómo deseas iniciar el control?</h4>
            <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => fileInputRef.current.click()} className="bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 font-semibold transition shadow-md hover:shadow-lg flex items-center gap-2">
                    <Icon path="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" className="w-6 h-6"/> Cargar desde Archivo CSV
                </button>
                <button onClick={() => setMode('manual')} className="bg-green-600 text-white py-3 px-6 rounded-md hover:bg-green-700 font-semibold transition shadow-md hover:shadow-lg flex items-center gap-2">
                    <Icon path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" className="w-6 h-6"/> Iniciar Conteo Manual
                </button>
            </div>
            <input type="file" ref={fileInputRef} accept=".csv" className="hidden" onChange={handleFileParse} />
        </div>
    );

    const renderFileMode = () => (
        <>
            {/* Vista de Tabla para Escritorio */}
            <div className="hidden md:block overflow-y-auto flex-grow">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm text-left text-stone-500">
                        <thead className="text-xs text-stone-700 uppercase bg-stone-100 sticky top-0 z-10">
                            <tr>
                                <th scope="col" className="px-4 py-3 w-2/5">Producto</th>
                                <th scope="col" className="px-4 py-3 text-center">Stock Sistema</th>
                                <th scope="col" className="px-4 py-3 text-center">Stock Físico</th>
                                <th scope="col" className="px-4 py-3 text-center">Diferencia</th>
                                <th scope="col" className="px-4 py-3">Observación</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.id} className={`border-b ${item.difference !== 0 && item.stockPhysical !== '' ? 'bg-yellow-50' : 'bg-white'} hover:bg-stone-50`}>
                                    <td scope="row" className="px-4 py-4 font-medium text-stone-900 whitespace-normal break-words">{item.name}</td>
                                    <td className="px-4 py-4 text-center">{item.stockSystem}</td>
                                    <td className="px-4 py-4">
                                        <input type="number" value={item.stockPhysical} onChange={(e) => handleItemChange(item.id, 'stockPhysical', e.target.value)} className="w-24 mx-auto text-center p-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-blue-500"/>
                                    </td>
                                    <td className={`px-4 py-4 text-center font-bold ${item.difference > 0 ? 'text-green-600' : item.difference < 0 ? 'text-red-600' : 'text-stone-700'}`}>
                                        {item.stockPhysical !== '' ? (item.difference > 0 ? `+${item.difference}` : item.difference) : '-'}
                                    </td>
                                    <td className="px-4 py-4">
                                        {item.difference !== 0 && item.stockPhysical !== '' && (
                                            <input type="text" value={item.observation} onChange={(e) => handleItemChange(item.id, 'observation', e.target.value)} className="w-full p-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-yellow-500" placeholder="Detalle..."/>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Vista de Tarjetas para Móvil */}
            <div className="md:hidden space-y-4 overflow-y-auto flex-grow">
                {items.map(item => (
                    <div key={item.id} className="bg-white p-4 rounded-lg shadow border">
                        <h4 className="font-bold text-stone-800 break-words">{item.name}</h4>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                            <div>
                                <label className="text-xs text-stone-500">Stock Sistema</label>
                                <p className="font-semibold text-lg">{item.stockSystem}</p>
                            </div>
                            <div>
                                <label className="text-xs text-stone-500">Diferencia</label>
                                <p className={`font-bold text-lg ${item.difference > 0 ? 'text-green-600' : item.difference < 0 ? 'text-red-600' : 'text-stone-700'}`}>
                                    {item.stockPhysical !== '' ? (item.difference > 0 ? `+${item.difference}` : item.difference) : '-'}
                                </p>
                            </div>
                            <div className="col-span-2">
                                <label className="text-sm font-medium text-stone-700">Stock Físico</label>
                                <input type="number" value={item.stockPhysical} onChange={(e) => handleItemChange(item.id, 'stockPhysical', e.target.value)} className="w-full text-center p-2 border border-stone-300 rounded-md text-lg focus:ring-2 focus:ring-blue-500"/>
                            </div>
                            {item.difference !== 0 && item.stockPhysical !== '' && (
                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-stone-700">Observación</label>
                                    <input type="text" value={item.observation} onChange={(e) => handleItemChange(item.id, 'observation', e.target.value)} className="w-full p-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-yellow-500" placeholder="Detalle..."/>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-end gap-4 pt-4 border-t mt-4 flex-shrink-0">
                <button onClick={onClose} className="bg-stone-300 text-stone-800 py-2 px-4 rounded-md hover:bg-stone-400">Cancelar</button>
                <button onClick={handleSaveAndComplete} disabled={isProcessing} className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 font-bold">{isProcessing ? 'Guardando...' : 'Guardar y Completar'}</button>
            </div>
        </>
    );
    
    const renderManualMode = () => (
        <>
            {product ? (
                <div className="mb-4 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700">Control en curso</p>
                    <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <h4 className="text-lg font-bold text-slate-900">{product.name}</h4>
                            <p className="mt-1 text-xs text-slate-500">{buildProductIdentityLabel(product)}</p>
                            <p className="mt-1 text-sm text-slate-600">El sistema ya toma este producto y su stock esperado. Vos solo verificás el conteo físico.</p>
                        </div>
                        <div className="rounded-2xl border border-cyan-200 bg-white px-3 py-2 text-right">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Stock del sistema</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{items[0]?.stockSystem ?? resolveSystemStockForProduct(product)}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="p-4 border-b mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 items-end gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-stone-700">Nombre del Producto</label>
                            <input type="text" value={manualItemName} onChange={(e) => setManualItemName(e.target.value)} className="mt-1 w-full p-2 border border-stone-300 rounded-md"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-stone-700">Stock Sistema</label>
                            <input type="number" value={manualItemSystemQty} onChange={(e) => setManualItemSystemQty(e.target.value)} placeholder="Ej: 100" className="mt-1 w-full p-2 border border-stone-300 rounded-md"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-stone-700">Stock Físico</label>
                            <input type="number" value={manualItemQty} onChange={(e) => setManualItemQty(e.target.value)} placeholder="Ej: 98" className="mt-1 w-full p-2 border border-stone-300 rounded-md"/>
                        </div>
                    </div>
                    <button onClick={handleAddManualItem} className="mt-4 w-full md:w-auto bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 flex items-center justify-center gap-2">
                        <Icon path="M12 4.5v15m7.5-7.5h-15" className="w-5 h-5" /> Agregar a la lista
                    </button>
                </div>
            )}
            <div className="overflow-y-auto flex-grow">
                {/* Desktop Table */}
                <table className="hidden md:table w-full text-sm text-left text-stone-500">
                    <thead className="text-xs text-stone-700 uppercase bg-stone-100 sticky top-0">
                        <tr>
                            <th scope="col" className="px-4 py-3">Producto</th>
                            <th scope="col" className="px-4 py-3 text-center">Stock Sistema</th>
                            <th scope="col" className="px-4 py-3 text-center">Stock Físico</th>
                            <th scope="col" className="px-4 py-3 text-center">Diferencia</th>
                            <th scope="col" className="px-4 py-3">Observación</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan="5" className="text-center p-8 text-stone-500">Aún no se han agregado productos.</td></tr>
                        )}
                        {items.map((item) => (
                        <tr key={item.id} className={`border-b ${item.difference !== 0 && item.stockPhysical !== '' ? 'bg-yellow-50' : 'bg-white'} hover:bg-stone-50`}>
                            <th scope="row" className="px-4 py-4 font-medium text-stone-900 whitespace-normal break-words">{item.name}</th>
                            <td className="px-4 py-4 text-center">{item.stockSystem}</td>
                            <td className="px-4 py-4">
                                <input type="number" value={item.stockPhysical} onChange={(e) => handleItemChange(item.id, 'stockPhysical', e.target.value)} className="w-24 mx-auto text-center p-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-blue-500"/>
                            </td>
                            <td className={`px-4 py-4 text-center font-bold ${item.difference > 0 ? 'text-green-600' : item.difference < 0 ? 'text-red-600' : 'text-stone-700'}`}>
                                {item.stockPhysical !== '' ? (item.difference > 0 ? `+${item.difference}` : item.difference) : '-'}
                            </td>
                            <td className="px-4 py-4">
                                {item.difference !== 0 && item.stockPhysical !== '' && (
                                    <input type="text" value={item.observation} onChange={(e) => handleItemChange(item.id, 'observation', e.target.value)} className="w-full p-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-yellow-500" placeholder="Detalle..."/>
                                )}
                            </td>
                        </tr>
                        ))}
                    </tbody>
                </table>
                {/* Mobile Cards */}
                <div className="md:hidden space-y-3">
                    {items.length === 0 && <p className="text-center p-8 text-stone-500">Aún no se han agregado productos.</p>}
                    {items.map(item => (
                        <div key={item.id} className="bg-slate-50 p-3 rounded-md border">
                            <p className="font-semibold text-stone-800">{item.name}</p>
                            <div className="grid grid-cols-2 gap-3 mt-2">
                                <div>
                                    <span className="text-xs text-stone-500">Sistema</span>
                                    <p className="text-base font-semibold text-stone-800">{item.stockSystem}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-stone-500">Diferencia</span>
                                    <p className={`text-base font-bold ${item.difference > 0 ? 'text-green-600' : item.difference < 0 ? 'text-red-600' : 'text-stone-700'}`}>
                                        {item.stockPhysical !== '' ? (item.difference > 0 ? `+${item.difference}` : item.difference) : '-'}
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-stone-700">Stock físico</label>
                                    <input type="number" value={item.stockPhysical} onChange={(e) => handleItemChange(item.id, 'stockPhysical', e.target.value)} className="mt-1 w-full text-center p-2 border border-stone-300 rounded-md text-lg focus:ring-2 focus:ring-blue-500"/>
                                </div>
                                {item.difference !== 0 && item.stockPhysical !== '' && (
                                    <div className="col-span-2">
                                        <label className="text-sm font-medium text-stone-700">Observación</label>
                                        <input type="text" value={item.observation} onChange={(e) => handleItemChange(item.id, 'observation', e.target.value)} className="mt-1 w-full p-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-yellow-500" placeholder="Detalle..."/>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex justify-end gap-4 pt-4 border-t mt-4 flex-shrink-0">
                <button onClick={onClose} className="bg-stone-300 text-stone-800 py-2 px-4 rounded-md hover:bg-stone-400">Cancelar</button>
                <button onClick={handleSaveAndComplete} disabled={isProcessing || items.length === 0} className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 font-bold disabled:bg-green-300 disabled:cursor-not-allowed">
                    {isProcessing ? 'Guardando...' : 'Guardar y Completar'}
                </button>
            </div>
        </>
    );

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-5xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4 pb-4 border-b flex-shrink-0">
                    <div>
                        <h3 className="text-xl sm:text-2xl font-bold text-stone-900">Control de Stock: {product.name}</h3>
                        <p className="mt-1 text-xs text-stone-500">{buildProductIdentityLabel(product)}</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-200"><Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6"/></button>
                </div>
                {mode === 'initial' && !product && renderInitial()}
                {mode === 'file' && renderFileMode()}
                {mode === 'manual' && renderManualMode()}
            </div>
        </div>
    );
};

export const HistoryListModal = ({ product, show, onClose, showMessage, companyId = 'default' }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedControlId, setExpandedControlId] = useState(null);

    useEffect(() => {
        if (show && product) {
            setLoading(true);
            const historyRef = collection(
                db,
                product?._source === 'legacy'
                    ? legacyStockProductControlsCollectionPath(appId, product.id)
                    : resolveStockControlsCollectionPath(companyId)
            );
            const baseQuery = product?._source === 'legacy'
                ? query(historyRef, orderBy('endTime', 'desc'))
                : query(historyRef, where('productId', '==', product.id));
            const unsubscribe = onSnapshot(baseQuery, (snapshot) => {
                const nextHistory = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => {
                        const timeA = a?.endTime?.toDate ? a.endTime.toDate().getTime() : 0;
                        const timeB = b?.endTime?.toDate ? b.endTime.toDate().getTime() : 0;
                        return timeB - timeA;
                    });
                setHistory(nextHistory);
                setLoading(false);
            }, (error) => {
                console.error('Error al cargar historial de stock:', error);
                setHistory([]);
                setLoading(false);
                showMessage('No se pudo cargar el historial del producto.', 'error');
            });
            return () => unsubscribe();
        }
    }, [show, product, companyId]);

    const handleToggleDetails = (controlId) => {
        setExpandedControlId(prevId => (prevId === controlId ? null : controlId));
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-60 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4 pb-4 border-b">
                    <h3 className="text-2xl font-bold text-stone-900">Historial de controles: {product.name}</h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-200"><Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6"/></button>
                </div>
                <div className="overflow-y-auto flex-grow">
                    {loading ? <p>Cargando historial...</p> : history.length === 0 ? <p className="text-center text-stone-500 py-8">No hay controles registrados.</p> : (
                        <ul className="space-y-3">
                            {history.map(control => (
                                <li key={control.id} className="p-4 rounded-md shadow-sm border bg-white">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-semibold">{getControlHeadline(control)}</p>
                                            <p className="text-xs text-stone-500">Tipo: {getControlTypeLabel(control)}</p>
                                        </div>
                                        <button onClick={() => handleToggleDetails(control.id)} className="bg-indigo-500 text-white py-2 px-4 rounded-md text-sm">
                                            {expandedControlId === control.id ? 'Ocultar Detalles' : 'Ver Detalles'}
                                        </button>
                                    </div>
                                    {expandedControlId === control.id && (
                                        <ControlDetailView control={{...control, productName: product.name}} showMessage={showMessage} />
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ProductHistoryModal = ({ product, show, onClose, showMessage, companyId = 'default' }) => {
    if (!show || !product) return null;
    return <ProductLifecycleModal product={product} show={show} onClose={onClose} showMessage={showMessage} companyId={companyId} />;
};

const PRODUCT_MOVEMENT_META = {
    purchase_pending_created: {
        label: 'Compra registrada',
        tone: 'border-amber-200 bg-amber-50 text-amber-700',
    },
    purchase_assigned_to_route: {
        label: 'Retiro de compra asignado',
        tone: 'border-sky-200 bg-sky-50 text-sky-700',
    },
    purchase_pickup_started: {
        label: 'Retiro de compra en curso',
        tone: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    },
    purchase_received: {
        label: 'Ingreso validado',
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    },
    stock_outbound_prepared: {
        label: 'Pedido de salida listo',
        tone: 'border-violet-200 bg-violet-50 text-violet-700',
    },
    stock_outbound_reserved: {
        label: 'Salida reservada',
        tone: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    },
    stock_outbound_assigned_to_route: {
        label: 'Entrega asignada',
        tone: 'border-sky-200 bg-sky-50 text-sky-700',
    },
    stock_outbound_started: {
        label: 'Salida en tránsito',
        tone: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700',
    },
    stock_outbound_delivered: {
        label: 'Entrega completada',
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    },
    stock_outbound_cancelled: {
        label: 'Salida cancelada',
        tone: 'border-rose-200 bg-rose-50 text-rose-700',
    },
    stock_outbound_replanned: {
        label: 'Salida reprogramada',
        tone: 'border-orange-200 bg-orange-50 text-orange-700',
    },
};

const getMovementTimestampMs = (entry = {}) => {
    const sources = [
        entry.appliedAt,
        entry.createdAt,
        entry.updatedAt,
        entry.endTime,
        entry.startTime,
    ];
    for (const source of sources) {
        if (!source) continue;
        if (typeof source?.toDate === 'function') {
            try {
                return source.toDate().getTime();
            } catch {
                continue;
            }
        }
        const parsed = new Date(source);
        if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
    }
    return 0;
};

const formatProductHistoryDate = (value) => {
    const ms = getMovementTimestampMs({ appliedAt: value });
    if (!ms) return 'Sin fecha';
    return new Date(ms).toLocaleString('es-AR');
};

const formatProductHistoryMoney = (value) => {
    const amount = Number(value || 0);
    if (!amount) return '—';
    try {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `$${amount}`;
    }
};

const toSafeMovementNumber = (value) => {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getStockBucketSnapshot = (buckets = {}) => ({
    physicalQty: toSafeMovementNumber(buckets?.physicalQty),
    pendingInboundQty: toSafeMovementNumber(buckets?.pendingInboundQty),
    inboundTransitQty: toSafeMovementNumber(buckets?.inboundTransitQty),
    pendingOutboundQty: toSafeMovementNumber(buckets?.pendingOutboundQty),
    outboundTransitQty: toSafeMovementNumber(buckets?.outboundTransitQty),
});

const getMovementBucketImpact = (movement = {}) => {
    const impact = movement?.stockBucketsImpact || {};
    return {
        physicalQty: toSafeMovementNumber(impact?.physicalDelta),
        pendingInboundQty: toSafeMovementNumber(impact?.pendingInboundDelta),
        inboundTransitQty: toSafeMovementNumber(impact?.inboundTransitDelta),
        pendingOutboundQty: toSafeMovementNumber(impact?.pendingOutboundDelta),
        outboundTransitQty: toSafeMovementNumber(impact?.outboundTransitDelta),
    };
};

const subtractBucketImpact = (buckets = {}, impact = {}) => ({
    physicalQty: Math.max(0, toSafeMovementNumber(buckets.physicalQty) - toSafeMovementNumber(impact.physicalQty)),
    pendingInboundQty: Math.max(0, toSafeMovementNumber(buckets.pendingInboundQty) - toSafeMovementNumber(impact.pendingInboundQty)),
    inboundTransitQty: Math.max(0, toSafeMovementNumber(buckets.inboundTransitQty) - toSafeMovementNumber(impact.inboundTransitQty)),
    pendingOutboundQty: Math.max(0, toSafeMovementNumber(buckets.pendingOutboundQty) - toSafeMovementNumber(impact.pendingOutboundQty)),
    outboundTransitQty: Math.max(0, toSafeMovementNumber(buckets.outboundTransitQty) - toSafeMovementNumber(impact.outboundTransitQty)),
});

const formatMovementQuantityLabel = (movement = {}) => {
    const quantity = Math.abs(toSafeMovementNumber(movement.quantity));
    if (!quantity) return '0 unidades';

    const type = String(movement?.movementType || '').trim().toLowerCase();
    const operationReason = String(movement?.operationReason || movement?.operationType || '').trim().toLowerCase();
    const looksOutboundSale =
        type.startsWith('purchase_') &&
        (
            operationReason.includes('venta') ||
            Boolean(movement?.saleUnitSnapshot || movement?.saleTotalSnapshot || movement?.marginSnapshot) ||
            Boolean(String(movement?.customerNameSnapshot || '').trim()) ||
            Boolean(String(movement?.referenceCodeSnapshot || '').trim())
        );
    const prefix =
        looksOutboundSale
            ? '-'
            : type.startsWith('purchase_')
                ? '+'
                : type.startsWith('stock_outbound_')
                    ? '-'
                    : '';
    return `${prefix}${quantity} ${quantity === 1 ? 'unidad' : 'unidades'}`;
};

const formatBucketsAfterEventLabel = (buckets = {}) => {
    const physicalQty = toSafeMovementNumber(buckets.physicalQty);
    const parts = [`${physicalQty} guardado`];

    if (toSafeMovementNumber(buckets.pendingInboundQty) > 0) {
        parts.push(`${toSafeMovementNumber(buckets.pendingInboundQty)} pendiente retiro de compra`);
    }
    if (toSafeMovementNumber(buckets.inboundTransitQty) > 0) {
        parts.push(`${toSafeMovementNumber(buckets.inboundTransitQty)} en tránsito ingreso`);
    }
    if (toSafeMovementNumber(buckets.pendingOutboundQty) > 0) {
        parts.push(`${toSafeMovementNumber(buckets.pendingOutboundQty)} pendiente salida`);
    }
    if (toSafeMovementNumber(buckets.outboundTransitQty) > 0) {
        parts.push(`${toSafeMovementNumber(buckets.outboundTransitQty)} en tránsito salida`);
    }

    return parts.join(' · ');
};

const isSaleLikeLegacyPurchaseMovement = (movement = {}) => {
    const rawType = String(movement?.movementType || '').trim().toLowerCase();
    if (!rawType.startsWith('purchase_')) return false;
    const operationReason = String(movement?.operationReason || movement?.operationType || '').trim().toLowerCase();
    return (
        operationReason.includes('venta') ||
        Boolean(movement?.saleUnitSnapshot || movement?.saleTotalSnapshot || movement?.marginSnapshot) ||
        Boolean(String(movement?.customerNameSnapshot || '').trim()) ||
        Boolean(String(movement?.referenceCodeSnapshot || '').trim())
    );
};

const hasMatchingOutboundMovement = (movement = {}, allMovements = [], expectedType = '') => {
    const movementMs = getMovementTimestampMs(movement);
    const routeId = String(movement?.routeId || '').trim();
    const quantity = toSafeMovementNumber(movement?.quantity);

    return (allMovements || []).some((candidate) => {
        if (String(candidate?.movementType || '').trim().toLowerCase() !== expectedType) return false;
        const candidateMs = getMovementTimestampMs(candidate);
        const sameRoute = routeId && routeId === String(candidate?.routeId || '').trim();
        const sameReference =
            String(movement?.referenceCodeSnapshot || '').trim() &&
            String(movement?.referenceCodeSnapshot || '').trim() === String(candidate?.referenceCodeSnapshot || '').trim();
        const sameQty = quantity > 0 && quantity === toSafeMovementNumber(candidate?.quantity);
        const closeEnough = Math.abs(candidateMs - movementMs) <= 10 * 60 * 1000;
        return closeEnough && sameQty && (sameRoute || sameReference);
    });
};

const shouldHideMovementFromTimeline = (movement = {}, allMovements = []) => {
    if (!isSaleLikeLegacyPurchaseMovement(movement)) return false;
    const rawType = String(movement?.movementType || '').trim().toLowerCase();
    if (rawType === 'purchase_assigned_to_route') {
        return hasMatchingOutboundMovement(movement, allMovements, 'stock_outbound_assigned_to_route');
    }
    if (rawType === 'purchase_received') {
        return hasMatchingOutboundMovement(movement, allMovements, 'stock_outbound_delivered');
    }
    if (rawType === 'purchase_pending_created') {
        return hasMatchingOutboundMovement(movement, allMovements, 'stock_outbound_prepared');
    }
    if (rawType === 'purchase_pickup_started') {
        return hasMatchingOutboundMovement(movement, allMovements, 'stock_outbound_started');
    }
    return false;
};

const resolveMovementBucketImpact = (movement = {}) => {
    if (!isSaleLikeLegacyPurchaseMovement(movement)) {
        return getMovementBucketImpact(movement);
    }

    const quantity = toSafeMovementNumber(movement?.quantity);
    const rawType = String(movement?.movementType || '').trim().toLowerCase();
    if (rawType === 'purchase_pickup_started') {
        return {
            physicalQty: -quantity,
            pendingInboundQty: 0,
            inboundTransitQty: 0,
            pendingOutboundQty: -quantity,
            outboundTransitQty: quantity,
        };
    }
    if (rawType === 'purchase_received') {
        return {
            physicalQty: 0,
            pendingInboundQty: 0,
            inboundTransitQty: 0,
            pendingOutboundQty: 0,
            outboundTransitQty: -quantity,
        };
    }
    if (rawType === 'purchase_assigned_to_route' || rawType === 'purchase_pending_created') {
        return {
            physicalQty: 0,
            pendingInboundQty: 0,
            inboundTransitQty: 0,
            pendingOutboundQty: 0,
            outboundTransitQty: 0,
        };
    }
    return getMovementBucketImpact(movement);
};

const resolveMovementMeta = (movement = {}) => {
    const rawType = String(movement?.movementType || '').trim();
    const fallbackMeta = PRODUCT_MOVEMENT_META[rawType] || {
        label: String(rawType || 'Movimiento').replace(/_/g, ' '),
        tone: 'border-slate-200 bg-slate-50 text-slate-700',
    };

    const looksOutboundSale = isSaleLikeLegacyPurchaseMovement(movement);

    if (!looksOutboundSale) {
        return fallbackMeta;
    }

    if (rawType === 'purchase_pending_created') return PRODUCT_MOVEMENT_META.stock_outbound_prepared;
    if (rawType === 'purchase_assigned_to_route') return PRODUCT_MOVEMENT_META.stock_outbound_assigned_to_route;
    if (rawType === 'purchase_pickup_started') return PRODUCT_MOVEMENT_META.stock_outbound_started;
    if (rawType === 'purchase_received') return PRODUCT_MOVEMENT_META.stock_outbound_delivered;
    return fallbackMeta;
};

const getMovementFlowKind = (movement = {}) => {
    const type = String(movement?.movementType || '').trim().toLowerCase();
    const resolvedLabel = String(movement?.resolvedMovementMeta?.label || '').trim().toLowerCase();
    if (
        type.startsWith('purchase_') ||
        resolvedLabel.includes('compra') ||
        resolvedLabel.includes('retiro') ||
        resolvedLabel.includes('ingreso')
    ) {
        return 'inbound';
    }
    if (
        type.startsWith('stock_outbound_') ||
        resolvedLabel.includes('salida') ||
        resolvedLabel.includes('entrega')
    ) {
        return 'outbound';
    }
    return 'special';
};

const getMovementCycleTone = (kind = 'special') => {
    if (kind === 'inbound') {
        return {
            shell: 'border-emerald-200 bg-[linear-gradient(180deg,rgba(236,253,245,0.98),rgba(255,255,255,0.96))]',
            badge: 'border-emerald-200 bg-emerald-50 text-emerald-700',
            title: 'text-emerald-800',
            accent: 'bg-emerald-500',
            stockChip: 'border-emerald-200 bg-emerald-100 text-emerald-900',
        };
    }
    if (kind === 'outbound') {
        return {
            shell: 'border-violet-200 bg-[linear-gradient(180deg,rgba(245,243,255,0.98),rgba(255,255,255,0.96))]',
            badge: 'border-violet-200 bg-violet-50 text-violet-700',
            title: 'text-violet-800',
            accent: 'bg-violet-500',
            stockChip: 'border-violet-200 bg-violet-100 text-violet-900',
        };
    }
    return {
        shell: 'border-slate-200 bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(255,255,255,0.96))]',
        badge: 'border-slate-200 bg-slate-50 text-slate-700',
        title: 'text-slate-800',
        accent: 'bg-slate-500',
        stockChip: 'border-slate-200 bg-slate-100 text-slate-900',
    };
};

const resolveSpecialCycleTitle = (items = []) => {
    const joinedText = (items || [])
        .flatMap((movement) => [
            String(movement?.movementType || ''),
            String(movement?.operationReason || ''),
            String(movement?.operationType || ''),
            String(movement?.resolvedMovementMeta?.label || ''),
            String(movement?.note || ''),
        ])
        .join(' ')
        .toLowerCase();

    if (/garant/.test(joinedText)) return 'Ciclo de garantía';
    if (/repar/.test(joinedText)) return 'Ciclo de reparación';
    if (/devol|return/.test(joinedText)) return 'Ciclo de devolución';
    if (/rotur|damage|incident|incid/.test(joinedText)) return 'Ciclo de incidente';
    return 'Evento especial';
};

const buildCycleIntegritySummary = (group = {}) => {
    const items = Array.isArray(group.items) ? group.items : [];
    const movementTypes = items.map((movement) => String(movement?.movementType || '').trim().toLowerCase());
    const closedBuckets = getStockBucketSnapshot(items[0]?.timelineBucketsAfter || {});

    if (group.flowKind === 'inbound') {
        const hasValidatedInbound = movementTypes.includes('purchase_received');
        if (hasValidatedInbound) {
            const hasOpenInboundBuckets = closedBuckets.pendingInboundQty > 0 || closedBuckets.inboundTransitQty > 0;
            if (hasOpenInboundBuckets) {
                return {
                    tone: 'warning',
                    label: 'Revisar consistencia',
                    detail: 'El ingreso quedó validado, pero todavía aparecen cantidades pendientes o en tránsito.',
                };
            }
            return {
                tone: 'ok',
                label: 'Cierre consistente',
                detail: 'El ingreso quedó validado y el stock cerró sin pendientes abiertos.',
            };
        }

        if (movementTypes.includes('purchase_pickup_started')) {
            return {
                tone: 'info',
                label: 'Ciclo en curso',
                detail: 'El retiro de compra ya comenzó, pero todavía falta validar el ingreso en depósito.',
            };
        }
    }

    if (group.flowKind === 'outbound') {
        const hasDeliveredOutbound = movementTypes.includes('stock_outbound_delivered');
        if (hasDeliveredOutbound) {
            const hasOpenOutboundBuckets = closedBuckets.pendingOutboundQty > 0 || closedBuckets.outboundTransitQty > 0;
            if (hasOpenOutboundBuckets) {
                return {
                    tone: 'warning',
                    label: 'Revisar consistencia',
                    detail: 'La entrega figura completada, pero todavía quedaron cantidades pendientes o en tránsito.',
                };
            }
            return {
                tone: 'ok',
                label: 'Cierre consistente',
                detail: 'La entrega cerró bien y el stock quedó alineado con la salida.',
            };
        }

        if (movementTypes.includes('stock_outbound_assigned_to_route')) {
            return {
                tone: 'info',
                label: 'Ciclo en curso',
                detail: 'La salida ya fue asignada. Falta completar la entrega para cerrar el ciclo.',
            };
        }
    }

    if (group.flowKind === 'special') {
        return {
            tone: 'info',
            label: 'Seguimiento especial',
            detail: 'Este ciclo requiere lectura puntual según el tipo de incidente o gestión especial.',
        };
    }

    return null;
};

const CYCLE_INTEGRITY_TONE = {
    ok: 'border-emerald-200 bg-emerald-100 text-emerald-900',
    warning: 'border-amber-200 bg-amber-100 text-amber-900',
    info: 'border-sky-200 bg-sky-100 text-sky-900',
};

const buildMovementCycleGroups = (movements = []) => {
    const groups = [];
    let currentGroup = null;

    (movements || []).forEach((movement) => {
        const flowKind = getMovementFlowKind(movement);
        const routeKey = String(movement?.routeId || movement?.routeCodeSnapshot || '').trim();
        const referenceKey = String(movement?.referenceCodeSnapshot || movement?.sourceLineId || '').trim();
        const cycleKey = `${flowKind}::${routeKey || 'no-route'}::${referenceKey || 'no-ref'}`;

        if (!currentGroup || currentGroup.cycleKey !== cycleKey) {
            currentGroup = {
                id: `${movement.id || Date.now()}-${groups.length}`,
                cycleKey,
                flowKind,
                routeKey,
                referenceKey,
                items: [],
            };
            groups.push(currentGroup);
        }

        currentGroup.items.push(movement);
    });

    return groups.map((group) => {
        const first = group.items[0] || {};
        const last = group.items[group.items.length - 1] || {};
        const tone = getMovementCycleTone(group.flowKind);
        const cycleTitle =
            group.flowKind === 'inbound'
                ? 'Ciclo de ingreso'
                : group.flowKind === 'outbound'
                    ? 'Ciclo de salida'
                    : resolveSpecialCycleTitle(group.items);
        return {
            ...group,
            tone,
            cycleTitle,
            integrity: buildCycleIntegritySummary(group),
            first,
            last,
            closedStateLabel: first?.resolvedBucketsAfterLabel || '',
            startedAtLabel: formatProductHistoryDate(last.appliedAt || last.createdAt),
            endedAtLabel: formatProductHistoryDate(first.appliedAt || first.createdAt),
            rangeLabel:
                formatProductHistoryDate(last.appliedAt || last.createdAt) === formatProductHistoryDate(first.appliedAt || first.createdAt)
                    ? formatProductHistoryDate(first.appliedAt || first.createdAt)
                    : `${formatProductHistoryDate(last.appliedAt || last.createdAt)} -> ${formatProductHistoryDate(first.appliedAt || first.createdAt)}`,
        };
    });
};

const formatOperationReason = (value = '') => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return '';
    const labels = {
        sale: 'Venta',
        warranty: 'Garantía',
        guarantee: 'Garantía',
        repair: 'Reparación',
        replacement: 'Reposición',
        transfer: 'Traslado',
        loan: 'Préstamo',
        return: 'Devolución',
        storage: 'Guardado',
    };
    return labels[normalized] || normalized.replace(/_/g, ' ');
};

const formatRouteTraceLabel = (value = '') => {
    const routeLabel = String(value || '').trim();
    if (!routeLabel) return '';
    return /^ruta\b/i.test(routeLabel) ? routeLabel : `Ruta ${routeLabel}`;
};

const formatSpecialBucketLabel = (type = '') => {
    const normalized = String(type || '').trim().toLowerCase();
    if (normalized === 'repair') return 'En garantía / reparación';
    if (normalized === 'returned') return 'Vuelta de garantía / devolución';
    if (normalized === 'damaged') return 'Rotura';
    return 'Especial';
};

const PURCHASE_TRACE_STATUS_LABELS = {
    pending: 'Pendiente',
    assigned: 'Asignada a ruta',
    applied: 'Aplicado',
    pickup_in_progress: 'Retiro de compra en curso',
    in_transit: 'Ingreso en tránsito',
    completed: 'Ingreso validado',
    cancelled: 'Cancelada',
};

const formatPurchaseTraceStatus = (value = '') => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return 'Origen registrado';
    return PURCHASE_TRACE_STATUS_LABELS[normalized] || normalized.replace(/_/g, ' ');
};

const getPersonInitials = (value = '', fallback = 'TR') =>
    String(value || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || fallback;

const TracePersonAvatar = ({ photoURL = '', displayName = 'Transportista', tone = 'slate' }) => {
    const shellTone = tone === 'emerald'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
        : 'border-violet-200 bg-violet-50 text-violet-700';

    if (String(photoURL || '').trim()) {
        return (
            <div className={`flex h-11 w-11 shrink-0 overflow-hidden rounded-2xl border ${shellTone}`}>
                <img
                    src={String(photoURL).trim()}
                    alt={displayName}
                    className="h-full w-full object-cover"
                />
            </div>
        );
    }

    return (
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${shellTone}`}>
            <span className="text-xs font-black tracking-[0.18em]">{getPersonInitials(displayName, 'TR')}</span>
        </div>
    );
};

const buildProductCycleSummary = (product = {}, movements = [], routesById = {}) => {
    const buckets = product.stockBuckets || {};
    const latestPurchase = movements.find((item) => item.movementType === 'purchase_received' || item.movementType === 'purchase_pending_created') || null;
    const latestOutbound = movements.find((item) => item.movementType === 'stock_outbound_delivered' || item.movementType === 'stock_outbound_started' || item.movementType === 'stock_outbound_prepared') || null;
    const latestInbound = movements.find((item) => item.movementType === 'purchase_received') || null;
    const latestDeparture = movements.find((item) => item.movementType === 'stock_outbound_started' || item.movementType === 'stock_outbound_delivered') || null;

    let lastStorageCycleLabel = 'Sin ciclo completo todavía';
    const inboundMs = getMovementTimestampMs(latestInbound || {});
    const departureMs = getMovementTimestampMs(latestDeparture || {});
    if (inboundMs && departureMs && departureMs >= inboundMs) {
        const days = Math.max(1, Math.round((departureMs - inboundMs) / 86400000));
        lastStorageCycleLabel = `${days} día${days === 1 ? '' : 's'} hasta su última salida`;
    } else if (inboundMs) {
        const days = Math.max(1, Math.round((Date.now() - inboundMs) / 86400000));
        lastStorageCycleLabel = `${days} día${days === 1 ? '' : 's'} guardado desde el último ingreso`;
    }

    const outboundRoute = latestOutbound?.routeId ? routesById[latestOutbound.routeId] : null;
    const inboundCount = movements.filter((item) => String(item.movementType || '').startsWith('purchase_')).length;
    const outboundCount = movements.filter((item) => String(item.movementType || '').startsWith('stock_outbound_')).length;
    const specialCount = movements.filter((item) => {
        const type = String(item.movementType || '');
        return type && !type.startsWith('purchase_') && !type.startsWith('stock_outbound_');
    }).length;
    return {
        stockLabel: `${Number(buckets.physicalQty || 0)} guardado · ${Number(buckets.pendingOutboundQty || 0)} pendiente · ${Number(buckets.outboundTransitQty || 0)} en tránsito`,
        latestPurchaseLabel: latestPurchase
            ? `${formatProductHistoryDate(latestPurchase.appliedAt || latestPurchase.createdAt)}${latestPurchase.unitCostSnapshot || latestPurchase.unitCost ? ` · ${formatProductHistoryMoney(latestPurchase.unitCostSnapshot || latestPurchase.unitCost)}` : ''}`
            : 'Sin compras registradas',
        latestOutboundLabel: latestOutbound
            ? [
                formatProductHistoryDate(latestOutbound.appliedAt || latestOutbound.createdAt),
                latestOutbound.customerNameSnapshot || latestOutbound.destinationNameSnapshot || '',
                outboundRoute?.transportName || outboundRoute?.vehicleLabel || '',
              ].filter(Boolean).join(' · ')
            : 'Todavía sin salidas',
        lastStorageCycleLabel,
        inboundCount,
        outboundCount,
        specialCount,
    };
};

const buildControlAdjustmentSummary = (controls = []) => {
    let controlsWithDifferences = 0;
    let differenceLines = 0;
    let positiveAdjustments = 0;
    let negativeAdjustments = 0;
    let latestDifferenceAt = null;

    (controls || []).forEach((control) => {
        let parsedDetails = [];
        try {
            parsedDetails = JSON.parse(control.countDetails || '[]');
        } catch {
            parsedDetails = [];
        }
        const differences = Array.isArray(parsedDetails)
            ? parsedDetails.filter((item) => Number(item?.difference || 0) !== 0)
            : [];

        if (!differences.length) return;
        controlsWithDifferences += 1;
        differenceLines += differences.length;
        positiveAdjustments += differences
            .filter((item) => Number(item?.difference || 0) > 0)
            .reduce((acc, item) => acc + Number(item?.difference || 0), 0);
        negativeAdjustments += differences
            .filter((item) => Number(item?.difference || 0) < 0)
            .reduce((acc, item) => acc + Math.abs(Number(item?.difference || 0)), 0);

        const currentMs = getMovementTimestampMs(control || {});
        if (currentMs && (!latestDifferenceAt || currentMs > latestDifferenceAt)) {
            latestDifferenceAt = currentMs;
        }
    });

    return {
        controlsWithDifferences,
        differenceLines,
        positiveAdjustments,
        negativeAdjustments,
        latestDifferenceAt,
    };
};

const buildSpecialCycleSummary = (product = {}, controls = [], movements = []) => {
    const buckets = product.stockBuckets || {};
    const controlSummary = buildControlAdjustmentSummary(controls);
    const specialMovements = (movements || []).filter((item) => {
        const type = String(item.movementType || '');
        return Boolean(type) && !type.startsWith('purchase_') && !type.startsWith('stock_outbound_');
    });
    const latestSpecialMovement = specialMovements.length ? specialMovements[0] : null;

    return {
        repairQty: Number(buckets.repairQty || 0),
        returnedQty: Number(buckets.returnedQty || 0),
        damagedQty: Number(buckets.damagedQty || 0),
        controlsWithDifferences: controlSummary.controlsWithDifferences,
        differenceLines: controlSummary.differenceLines,
        positiveAdjustments: controlSummary.positiveAdjustments,
        negativeAdjustments: controlSummary.negativeAdjustments,
        latestDifferenceLabel: controlSummary.latestDifferenceAt
            ? formatProductHistoryDate(controlSummary.latestDifferenceAt)
            : 'Sin diferencias registradas',
        latestSpecialMovementLabel: latestSpecialMovement
            ? `${PRODUCT_MOVEMENT_META[latestSpecialMovement.movementType]?.label || latestSpecialMovement.movementType} · ${formatProductHistoryDate(latestSpecialMovement.appliedAt || latestSpecialMovement.createdAt)}`
            : 'Todavía sin eventos especiales aplicados',
    };
};

const buildPurchaseBatches = (product = {}, purchaseLines = [], purchasesById = {}) => {
    const normalizedLines = (purchaseLines || [])
        .map((line) => {
            const receivedQuantity = Number(line.receivedQuantity || 0);
            const purchase = purchasesById[String(line.purchaseId || '')] || null;
            const purchaseDateValue =
                purchase?.purchaseDate ||
                purchase?.createdAt ||
                line?.closedAt ||
                line?.updatedAt ||
                line?.createdAt ||
                null;
            return {
                ...line,
                receivedQuantity,
                unitCost: Number(line.unitCost || 0),
                totalCost: Number(line.totalCost || 0),
                batchLabelSnapshot:
                    line.batchLabelSnapshot ||
                    (line.purchaseId && Number(line.lineOrder || 0) > 0
                        ? `Lote ${String(line.purchaseId).slice(0, 6).toUpperCase()} · línea ${Number(line.lineOrder || 0)}`
                        : ''),
                purchase,
                purchaseDateValue,
                purchaseDateMs: getMovementTimestampMs({ appliedAt: purchaseDateValue }),
            };
        })
        .filter((line) => line.receivedQuantity > 0)
        .sort((a, b) => b.purchaseDateMs - a.purchaseDateMs);

    return normalizedLines
        .map((line) => {
            const lastTraceLabel = line.lastMovementStatus
                ? formatPurchaseTraceStatus(line.lastMovementStatus)
                : formatPurchaseTraceStatus(line.status || line.purchase?.status);
            return {
            ...line,
            statusLabel: lastTraceLabel,
        };
        });
};

const ProductLifecycleModal = ({ product, show, onClose, showMessage, companyId = 'default' }) => {
    const [controlsHistory, setControlsHistory] = useState([]);
    const [movementsHistory, setMovementsHistory] = useState([]);
    const [purchaseLinesHistory, setPurchaseLinesHistory] = useState([]);
    const [purchasesById, setPurchasesById] = useState({});
    const [routesById, setRoutesById] = useState({});
    const [controllersById, setControllersById] = useState({});
    const [loading, setLoading] = useState(true);
    const [expandedControlId, setExpandedControlId] = useState(null);
    const [activeTab, setActiveTab] = useState('trace');

    useEffect(() => {
        if (!show || !product) return undefined;

        setLoading(true);
        setControlsHistory([]);
        setMovementsHistory([]);
        setPurchaseLinesHistory([]);
        setPurchasesById({});
        setRoutesById({});
        setControllersById({});
        setActiveTab('trace');

        let controlsLoaded = false;
        let movementsLoaded = false;
        let purchasesLoaded = false;
        const markLoaded = () => {
            if (controlsLoaded && movementsLoaded && purchasesLoaded) setLoading(false);
        };

        const controlsRef = collection(
            db,
            product?._source === 'legacy'
                ? legacyStockProductControlsCollectionPath(appId, product.id)
                : resolveStockControlsCollectionPath(companyId)
        );
        const controlsQuery = product?._source === 'legacy'
            ? query(controlsRef, orderBy('endTime', 'desc'))
            : query(controlsRef, where('productId', '==', product.id));

        const unsubscribeControls = onSnapshot(controlsQuery, (snapshot) => {
            const nextControls = snapshot.docs
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
                .sort((a, b) => getMovementTimestampMs(b) - getMovementTimestampMs(a));
            setControlsHistory(nextControls);
            controlsLoaded = true;
            markLoaded();
        }, (error) => {
            console.error('Error al cargar controles del producto:', error);
            setControlsHistory([]);
            controlsLoaded = true;
            markLoaded();
            showMessage('No se pudo cargar el historial físico del producto.', 'error');
        });

        const movementsRef = collection(db, resolveStockMovementsCollectionPath(companyId));
        const movementsQuery = query(movementsRef, where('productId', '==', product.id));
        const unsubscribeMovements = onSnapshot(movementsQuery, async (snapshot) => {
            const nextMovements = snapshot.docs
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
                .sort((a, b) => getMovementTimestampMs(b) - getMovementTimestampMs(a));
            setMovementsHistory(nextMovements);

            const uniqueRouteIds = Array.from(new Set(nextMovements.map((item) => String(item.routeId || '').trim()).filter(Boolean)));
            if (!uniqueRouteIds.length) {
                setRoutesById({});
                movementsLoaded = true;
                markLoaded();
                return;
            }

            try {
                const routeEntries = await Promise.all(
                    uniqueRouteIds.map(async (routeId) => {
                        const routeSnap = await getDoc(doc(db, resolveRouteDocPath(companyId, routeId)));
                        if (!routeSnap.exists()) return [routeId, null];
                        return [routeId, { id: routeSnap.id, ...routeSnap.data() }];
                    })
                );
                setRoutesById(
                    routeEntries.reduce((acc, [routeId, routeData]) => {
                        if (routeId && routeData) acc[routeId] = routeData;
                        return acc;
                    }, {})
                );
            } catch (error) {
                console.error('Error al cargar rutas del historial del producto:', error);
                setRoutesById({});
            } finally {
                movementsLoaded = true;
                markLoaded();
            }
        }, (error) => {
            console.error('Error al cargar movimientos del producto:', error);
            setMovementsHistory([]);
            setRoutesById({});
            movementsLoaded = true;
            markLoaded();
            showMessage('No se pudo cargar la trazabilidad operativa del producto.', 'error');
        });

        const purchaseLinesRef = collection(db, resolveStockPurchaseLinesCollectionPath(companyId));
        const purchaseLinesQuery = query(purchaseLinesRef, where('productId', '==', product.id));
        const unsubscribePurchaseLines = onSnapshot(purchaseLinesQuery, async (snapshot) => {
            const nextLines = snapshot.docs
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
                .sort((a, b) => getMovementTimestampMs(b) - getMovementTimestampMs(a));
            setPurchaseLinesHistory(nextLines);

            const uniquePurchaseIds = Array.from(new Set(nextLines.map((item) => String(item.purchaseId || '').trim()).filter(Boolean)));
            if (!uniquePurchaseIds.length) {
                setPurchasesById({});
                purchasesLoaded = true;
                markLoaded();
                return;
            }

            try {
                const purchaseEntries = await Promise.all(
                    uniquePurchaseIds.map(async (purchaseId) => {
                        const purchaseSnap = await getDoc(doc(db, resolveStockPurchaseDocPath(companyId, purchaseId)));
                        if (!purchaseSnap.exists()) return [purchaseId, null];
                        return [purchaseId, { id: purchaseSnap.id, ...purchaseSnap.data() }];
                    })
                );
                setPurchasesById(
                    purchaseEntries.reduce((acc, [purchaseId, purchaseData]) => {
                        if (purchaseId && purchaseData) acc[purchaseId] = purchaseData;
                        return acc;
                    }, {})
                );

                const uniquePurchaseRouteIds = Array.from(
                    new Set(
                        nextLines
                            .map((line) => String(line?.lastRouteId || '').trim())
                            .filter(Boolean)
                    )
                );
                if (uniquePurchaseRouteIds.length > 0) {
                    const routeEntries = await Promise.all(
                        uniquePurchaseRouteIds.map(async (routeId) => {
                            const routeSnap = await getDoc(doc(db, resolveRouteDocPath(companyId, routeId)));
                            if (!routeSnap.exists()) return [routeId, null];
                            return [routeId, { id: routeSnap.id, ...routeSnap.data() }];
                        })
                    );
                    setRoutesById((prev) => ({
                        ...prev,
                        ...routeEntries.reduce((acc, [routeId, routeData]) => {
                            if (routeId && routeData) acc[routeId] = routeData;
                            return acc;
                        }, {}),
                    }));
                }
            } catch (error) {
                console.error('Error al cargar partidas de compra del producto:', error);
                setPurchasesById({});
            } finally {
                purchasesLoaded = true;
                markLoaded();
            }
        }, (error) => {
            console.error('Error al cargar líneas de compra del producto:', error);
            setPurchaseLinesHistory([]);
            setPurchasesById({});
            purchasesLoaded = true;
            markLoaded();
            showMessage('No se pudo cargar el origen de compra del producto.', 'error');
        });

        return () => {
            unsubscribeControls();
            unsubscribeMovements();
            unsubscribePurchaseLines();
        };
    }, [show, product, companyId, showMessage]);

    useEffect(() => {
        if (!show || !product || product?._source === 'legacy') {
            setControllersById({});
            return undefined;
        }

        const controllerIds = Array.from(
            new Set(
                (controlsHistory || [])
                    .map((control) => String(control?.controllerId || '').trim())
                    .filter(Boolean)
            )
        );

        if (!controllerIds.length) {
            setControllersById({});
            return undefined;
        }

        let cancelled = false;

        const loadControllers = async () => {
            try {
                const controllerEntries = await Promise.all(
                    controllerIds.map(async (controllerId) => {
                        const controllerSnap = await getDoc(doc(db, resolveStockControllersCollectionPath(companyId), controllerId));
                        if (!controllerSnap.exists()) return [controllerId, null];
                        return [controllerId, { id: controllerSnap.id, ...controllerSnap.data() }];
                    })
                );

                if (cancelled) return;

                setControllersById(
                    controllerEntries.reduce((acc, [controllerId, controllerData]) => {
                        if (controllerId && controllerData) acc[controllerId] = controllerData;
                        return acc;
                    }, {})
                );
            } catch (error) {
                console.error('Error al cargar controladores del historial físico:', error);
                if (!cancelled) setControllersById({});
            }
        };

        loadControllers();

        return () => {
            cancelled = true;
        };
    }, [companyId, controlsHistory, product, show]);

    if (!show || !product) return null;

    const purchaseLinesProgress = (purchaseLinesHistory || []).reduce(
        (acc, line) => {
            const progress = getPurchaseLineProgress(line);
            acc.pendingInboundQty += Math.max(
                0,
                Number(progress.orderedNet || 0) -
                Number(progress.pickedUpQuantity || 0) -
                Number(progress.receivedQuantity || 0)
            );
            acc.inboundTransitQty += Math.max(0, Number(progress.pickedUpQuantity || 0) - Number(progress.receivedQuantity || 0));
            return acc;
        },
        { pendingInboundQty: 0, inboundTransitQty: 0 }
    );
    const summary = buildProductCycleSummary(product, movementsHistory, routesById);
    const specialSummary = buildSpecialCycleSummary(product, controlsHistory, movementsHistory);
    const currentBuckets = {
        ...(product.stockBuckets || {}),
        pendingInboundQty: purchaseLinesProgress.pendingInboundQty,
        inboundTransitQty: purchaseLinesProgress.inboundTransitQty,
    };
    const enrichedControlsHistory = (controlsHistory || []).map((control) => {
        const linkedController = controllersById[String(control?.controllerId || '').trim()] || null;
        return {
            ...control,
            resolvedControllerDisplayName: getControlResponsibleName({
                ...linkedController,
                ...control,
                adjustedByName: control?.adjustedByName,
                controllerDisplayNameSnapshot: control?.controllerDisplayNameSnapshot || linkedController?.displayName,
                controllerNameSnapshot: control?.controllerNameSnapshot || linkedController?.firstName,
                controllerLastNameSnapshot: control?.controllerLastNameSnapshot || linkedController?.lastName,
            }),
            resolvedControllerPhotoURL: String(
                control?.controllerPhotoURLSnapshot ||
                linkedController?.photoURL ||
                ''
            ).trim(),
        };
    });
    const purchaseBatches = buildPurchaseBatches(product, purchaseLinesHistory, purchasesById);
    const latestBatch = purchaseBatches[0] || null;
    const latestInboundMovement = movementsHistory.find((movement) =>
        ['purchase_received', 'purchase_pickup_started', 'purchase_pending_created', 'purchase_assigned_to_route']
            .includes(String(movement?.movementType || '').trim().toLowerCase())
    ) || null;
    const latestOutboundMovement = movementsHistory.find((movement) =>
        String(movement?.movementType || '').trim().toLowerCase().startsWith('stock_outbound_')
    ) || null;
    const latestInboundRoute = latestInboundMovement?.routeId
        ? routesById[latestInboundMovement.routeId]
        : latestBatch?.lastRouteId
            ? routesById[latestBatch.lastRouteId]
            : null;
    const latestOutboundRoute = latestOutboundMovement?.routeId ? routesById[latestOutboundMovement.routeId] : null;
    const latestInboundType = String(latestInboundMovement?.movementType || '').trim().toLowerCase();
    const latestInboundStageLabel =
        latestInboundType === 'purchase_received'
            ? 'Ingresó el producto'
            : latestInboundType === 'purchase_pickup_started'
                ? 'Ingreso en tránsito'
            : latestInboundType === 'purchase_assigned_to_route'
                ? 'Retiro de compra asignado'
                : 'Origen del producto';
    const latestInboundTransport =
        latestInboundMovement?.transportNameSnapshot ||
        latestInboundRoute?.transportName ||
        latestInboundRoute?.vehicleLabel ||
        latestInboundRoute?.transporterName ||
        latestInboundMovement?.supplierNameSnapshot ||
        latestBatch?.purchase?.supplierNameSnapshot ||
        latestBatch?.supplierNameSnapshot ||
        'Todavía sin transporte visible';
    const latestInboundDriver =
        latestInboundMovement?.driverNameSnapshot ||
        [latestInboundRoute?.driverName, latestInboundRoute?.driverLastName].filter(Boolean).join(' ').trim() ||
        (latestInboundType === 'purchase_received' || latestInboundType === 'purchase_pickup_started'
            ? 'Todavía sin chofer visible'
            : 'Sin chofer asociado');
    const latestInboundDriverPhotoURL =
        latestInboundMovement?.driverPhotoURLSnapshot ||
        latestInboundRoute?.driverPhotoURL ||
        latestInboundRoute?.currentDriverPhotoURL ||
        latestInboundRoute?.photoURL ||
        '';
    const latestOutboundTransport =
        latestOutboundMovement?.transportNameSnapshot ||
        latestOutboundRoute?.transportName ||
        latestOutboundRoute?.vehicleLabel ||
        latestOutboundRoute?.transporterName ||
        'Todavía sin transporte asignado';
    const latestOutboundDriver =
        latestOutboundMovement?.driverNameSnapshot ||
        [latestOutboundRoute?.driverName, latestOutboundRoute?.driverLastName].filter(Boolean).join(' ').trim() ||
        'Todavía sin chofer asignado';
    const latestOutboundDriverPhotoURL =
        latestOutboundMovement?.driverPhotoURLSnapshot ||
        latestOutboundRoute?.driverPhotoURL ||
        latestOutboundRoute?.currentDriverPhotoURL ||
        latestOutboundRoute?.photoURL ||
        '';
    const latestOutboundDestination =
        latestOutboundMovement?.destinationNameSnapshot ||
        latestOutboundMovement?.customerNameSnapshot ||
        'Todavía sin destino final';
    const latestOutboundRouteLabel = formatRouteTraceLabel(
        latestOutboundMovement?.routeCodeSnapshot || latestOutboundRoute?.name || ''
    ) || 'Todavía sin ruta asignada';
    const latestCostUnit = Number(
        latestOutboundMovement?.costUnitSnapshot ||
        latestBatch?.unitCost ||
        product?.defaultPurchaseCost ||
        0
    );
    const latestSaleUnit = Number(
        latestOutboundMovement?.saleUnitSnapshot ||
        product?.defaultSalePrice ||
        0
    );
    const latestMargin = Number(
        latestOutboundMovement?.marginSnapshot ||
        ((latestCostUnit > 0 && latestSaleUnit > 0 && Number(latestOutboundMovement?.quantity || 0) > 0)
            ? (latestSaleUnit - latestCostUnit) * Number(latestOutboundMovement.quantity || 0)
            : 0)
    );
    const visibleMovementsHistory = (movementsHistory || []).filter((movement) => !shouldHideMovementFromTimeline(movement, movementsHistory));
    const annotatedMovementsHistory = (() => {
        const baseBuckets = getStockBucketSnapshot(currentBuckets);
        let newerImpact = getStockBucketSnapshot({});

        return visibleMovementsHistory.map((movement) => {
            const bucketsAfterEvent = subtractBucketImpact(baseBuckets, newerImpact);
            const impact = resolveMovementBucketImpact(movement);
            newerImpact = {
                physicalQty: newerImpact.physicalQty + impact.physicalQty,
                pendingInboundQty: newerImpact.pendingInboundQty + impact.pendingInboundQty,
                inboundTransitQty: newerImpact.inboundTransitQty + impact.inboundTransitQty,
                pendingOutboundQty: newerImpact.pendingOutboundQty + impact.pendingOutboundQty,
                outboundTransitQty: newerImpact.outboundTransitQty + impact.outboundTransitQty,
            };

            return {
                ...movement,
                timelineBucketsAfter: bucketsAfterEvent,
                resolvedMovementMeta: resolveMovementMeta(movement),
                resolvedQuantityLabel: formatMovementQuantityLabel(movement),
                resolvedBucketsAfterLabel: formatBucketsAfterEventLabel(bucketsAfterEvent),
            };
        });
    })();
    const timelineSummary = annotatedMovementsHistory.reduce((acc, movement) => {
        const resolvedLabel = String(movement?.resolvedMovementMeta?.label || '').trim().toLowerCase();
        const isOutbound = resolvedLabel.includes('salida') || resolvedLabel.includes('entrega');
        const isInbound = resolvedLabel.includes('compra') || resolvedLabel.includes('retiro') || resolvedLabel.includes('ingreso');
        if (isOutbound) acc.outboundCount += 1;
        else if (isInbound) acc.inboundCount += 1;
        else acc.specialCount += 1;
        return acc;
    }, { inboundCount: 0, outboundCount: 0, specialCount: 0 });
    const movementCycleGroups = buildMovementCycleGroups(annotatedMovementsHistory);
    const isTraceTab = activeTab === 'trace';
    const isHistoryTab = activeTab === 'history';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
            <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)]">
                <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#172554_60%,#1e293b_100%)] px-6 py-5 text-white">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-cyan-200">Trazabilidad del producto</p>
                            <h3 className="mt-2 truncate text-3xl font-bold">{product.name}</h3>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                {buildProductIdentityParts(product).map((part) => (
                                    <span
                                        key={part}
                                        className="rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-100"
                                    >
                                        {part}
                                    </span>
                                ))}
                            </div>
                            <p className="mt-2 max-w-4xl text-sm text-slate-200">
                                Acá vive la traza completa del producto: origen de compra, permanencia en depósito, salidas, destino, ruta y validaciones físicas.
                            </p>
                        </div>
                        <button onClick={onClose} className="rounded-full border border-white/10 bg-white/10 p-2 text-white transition hover:bg-white/15">
                            <Icon path="M6 18L18 6M6 6l12 12" className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] px-6 py-6">
                    <div className="mb-6 flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setActiveTab('trace')}
                            className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                                isTraceTab
                                    ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                            }`}
                        >
                            Recorrido operativo
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                                isHistoryTab
                                    ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                            }`}
                        >
                            Cronología completa
                        </button>
                    </div>

                    {isHistoryTab ? (
                        <div className="mb-6 rounded-[24px] border border-slate-200 bg-white/90 px-5 py-4 shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Resumen cronológico</p>
                            <p className="mt-1 text-sm text-slate-600">
                                Acá ves la cronología del producto: qué compras lo formaron, qué movimientos tuvo y qué controles físicos se hicieron.
                            </p>
                        </div>
                    ) : null}

                    <div className={isTraceTab ? 'grid gap-4 lg:grid-cols-4' : 'hidden'}>
                        <div className="rounded-[24px] border border-slate-200 bg-white/90 px-4 py-4 shadow-sm">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Estado actual</p>
                            <p className="mt-2 text-sm font-semibold text-slate-800">{summary.stockLabel}</p>
                            <div className="mt-3 space-y-1 text-xs text-slate-500">
                                <p>{formatSpecialBucketLabel('repair')}: {Number(currentBuckets.repairQty || 0)}</p>
                                <p>{formatSpecialBucketLabel('returned')}: {Number(currentBuckets.returnedQty || 0)}</p>
                                <p>Rotura: {Number(currentBuckets.damagedQty || 0)}</p>
                            </div>
                        </div>
                        <div className="rounded-[24px] border border-emerald-200 bg-emerald-50/85 px-4 py-4 shadow-sm">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-600">Última compra</p>
                            <p className="mt-2 text-sm font-semibold text-emerald-800">{summary.latestPurchaseLabel}</p>
                        </div>
                        <div className="rounded-[24px] border border-violet-200 bg-violet-50/85 px-4 py-4 shadow-sm">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-600">Última salida</p>
                            <p className="mt-2 text-sm font-semibold text-violet-800">{summary.latestOutboundLabel}</p>
                        </div>
                        <div className="rounded-[24px] border border-cyan-200 bg-cyan-50/85 px-4 py-4 shadow-sm">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-600">Tiempo guardado</p>
                            <p className="mt-2 text-sm font-semibold text-cyan-800">{summary.lastStorageCycleLabel}</p>
                        </div>
                    </div>

                    <div className={isTraceTab ? 'mt-4 grid gap-3 lg:grid-cols-4' : 'hidden'}>
                        <div className="rounded-[24px] border border-slate-200 bg-white/90 px-4 py-4 shadow-sm">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Origen principal</p>
                            <p className="mt-2 text-sm font-semibold text-slate-800">
                                {latestBatch?.supplierNameSnapshot || latestBatch?.purchase?.supplierNameSnapshot || 'Sin proveedor visible'}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                                {latestBatch?.batchLabelSnapshot || latestBatch?.purchase?.sourceReference || 'Sin lote visible'}
                            </p>
                        </div>
                        <div className="rounded-[24px] border border-slate-200 bg-white/90 px-4 py-4 shadow-sm">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Última salida trazada</p>
                            <p className="mt-2 text-sm font-semibold text-slate-800">{latestOutboundDestination}</p>
                            <p className="mt-1 text-xs text-slate-500">{latestOutboundRouteLabel}</p>
                        </div>
                        <div className="rounded-[24px] border border-slate-200 bg-white/90 px-4 py-4 shadow-sm">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Transportistas trazados</p>
                            <div className="mt-3 space-y-3">
                                <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/70 px-3 py-3">
                                    <TracePersonAvatar
                                        photoURL={latestInboundDriverPhotoURL}
                                        displayName={latestInboundDriver}
                                        tone="emerald"
                                    />
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700">{latestInboundStageLabel}</p>
                                        <p className="truncate text-sm font-semibold text-slate-800">{latestInboundTransport}</p>
                                        <p className="truncate text-xs text-slate-500">{latestInboundDriver}</p>
                                    </div>
                                </div>
                                {latestOutboundMovement ? (
                                    <div className="flex items-center gap-3 rounded-2xl border border-violet-100 bg-violet-50/70 px-3 py-3">
                                        <TracePersonAvatar
                                            photoURL={latestOutboundDriverPhotoURL}
                                            displayName={latestOutboundDriver}
                                            tone="violet"
                                        />
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-700">Sacó el producto</p>
                                            <p className="truncate text-sm font-semibold text-slate-800">{latestOutboundTransport}</p>
                                            <p className="truncate text-xs text-slate-500">{latestOutboundDriver}</p>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className="rounded-[24px] border border-emerald-200 bg-emerald-50/85 px-4 py-4 shadow-sm">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-600">Lectura económica</p>
                            <p className="mt-2 text-sm font-semibold text-emerald-800">
                                Compra {formatProductHistoryMoney(latestCostUnit)} · Venta {formatProductHistoryMoney(latestSaleUnit)}
                            </p>
                            <p className={`mt-1 text-xs font-semibold ${latestMargin >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                                {latestMargin
                                    ? `Margen estimado ${formatProductHistoryMoney(latestMargin)}`
                                    : 'Todavía sin margen visible'}
                            </p>
                        </div>
                    </div>

                    <div className={isTraceTab ? 'mt-6 rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-sm' : 'hidden'}>
                        <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-600">Especiales y ajustes</p>
                                <h4 className="mt-1 text-xl font-bold text-slate-900">Incidentes, devoluciones y desvíos</h4>
                                <p className="mt-1 text-sm text-slate-500">
                                    Este bloque muestra lo que hoy ya sabemos con certeza: buckets especiales activos y diferencias reales detectadas en controles físicos.
                                </p>
                            </div>
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                                {specialSummary.repairQty + specialSummary.returnedQty + specialSummary.damagedQty} unidades en estados especiales
                            </span>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            <div className="rounded-[22px] border border-amber-200 bg-amber-50/90 px-4 py-4">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-700">En garantía</p>
                                <p className="mt-2 text-2xl font-bold text-amber-900">{specialSummary.repairQty}</p>
                                <p className="mt-1 text-xs text-amber-800/80">Unidades hoy apartadas para garantía, revisión o service.</p>
                            </div>
                            <div className="rounded-[22px] border border-cyan-200 bg-cyan-50/90 px-4 py-4">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-700">Vuelta de garantía</p>
                                <p className="mt-2 text-2xl font-bold text-cyan-900">{specialSummary.returnedQty}</p>
                                <p className="mt-1 text-xs text-cyan-800/80">Unidades que volvieron del circuito de garantía o quedaron marcadas como retorno.</p>
                            </div>
                            <div className="rounded-[22px] border border-rose-200 bg-rose-50/90 px-4 py-4">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-700">Rotura</p>
                                <p className="mt-2 text-2xl font-bold text-rose-900">{specialSummary.damagedQty}</p>
                                <p className="mt-1 text-xs text-rose-800/80">Unidades dañadas o inmovilizadas por incidente.</p>
                            </div>
                            <div className="rounded-[22px] border border-slate-200 bg-slate-50/90 px-4 py-4">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600">Ajustes físicos</p>
                                <p className="mt-2 text-2xl font-bold text-slate-900">{specialSummary.controlsWithDifferences}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                    {specialSummary.differenceLines} renglón{specialSummary.differenceLines === 1 ? '' : 'es'} con diferencia · último {specialSummary.latestDifferenceLabel}
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Última señal especial</p>
                                <p className="mt-1 text-sm font-semibold text-slate-800">{specialSummary.latestSpecialMovementLabel}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Ajustes positivos</p>
                                <p className="mt-1 text-sm font-semibold text-emerald-700">+{specialSummary.positiveAdjustments}</p>
                                <p className="mt-1 text-xs text-slate-500">Diferencias donde el físico superó al sistema.</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Ajustes negativos</p>
                                <p className="mt-1 text-sm font-semibold text-rose-700">-{specialSummary.negativeAdjustments}</p>
                                <p className="mt-1 text-xs text-slate-500">Diferencias donde el sistema quedó por encima del físico.</p>
                            </div>
                        </div>
                    </div>

                    <div className={isTraceTab ? 'mt-6 rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-sm' : 'hidden'}>
                        <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-600">Compras y origen</p>
                                <h4 className="mt-1 text-xl font-bold text-slate-900">Ingresos que formaron el producto</h4>
                                <p className="mt-1 text-sm text-slate-500">
                                    Acá se ven los ingresos reales que armaron este producto, cuánto costó cada origen y cuál fue su última traza operativa.
                                </p>
                            </div>
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                                {purchaseBatches.length} ingreso{purchaseBatches.length === 1 ? '' : 's'}
                            </span>
                        </div>

                        <div className="mt-4 space-y-3">
                            {loading ? (
                                <p className="py-8 text-center text-sm text-slate-500">Cargando partidas...</p>
                            ) : purchaseBatches.length === 0 ? (
                                <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                                    Este producto todavía no tiene partidas recibidas para mostrar.
                                </p>
                            ) : (
                                purchaseBatches.map((batch) => (
                                    <div key={batch.id} className="rounded-[24px] border border-slate-200 bg-slate-50/70 p-4">
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-700">
                                                        {batch.statusLabel}
                                                    </span>
                                                    {batch.purchase?.sourceReference ? (
                                                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                                            Ref. {batch.purchase.sourceReference}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <p className="mt-2 text-sm font-semibold text-slate-900">
                                                    {formatProductHistoryDate(batch.purchaseDateValue || batch.createdAt)} · {batch.supplierNameSnapshot || batch.purchase?.supplierNameSnapshot || 'Proveedor sin identificar'}
                                                </p>
                                                <p className="mt-1 text-xs text-slate-500">
                                                    {batch.batchLabelSnapshot || `Compra ${batch.purchaseId || 'sin id'} · línea ${Number(batch.lineOrder || 0) || '—'}`}
                                                </p>
                                            </div>
                                            <div className="text-right text-xs text-slate-500">
                                                <p>{batch.productNameSnapshot || product.name}</p>
                                                {batch.skuSnapshot ? <p>SKU {batch.skuSnapshot}</p> : null}
                                            </div>
                                        </div>

                                        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Ingreso</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-800">{Number(batch.receivedQuantity || 0)} unidades recibidas</p>
                                                <p className="mt-1 text-xs text-slate-500">Costo total {formatProductHistoryMoney(batch.totalCost || batch.unitCost * batch.receivedQuantity)}</p>
                                            </div>
                                            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Estado del origen</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-800">{batch.statusLabel}</p>
                                                <p className="mt-1 text-xs text-slate-500">
                                                    {batch.lastRouteCodeSnapshot ? `Última ruta ${batch.lastRouteCodeSnapshot}` : 'Ingreso ya incorporado a la historia del producto'}
                                                </p>
                                            </div>
                                            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Trazabilidad visible</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-800">
                                                    {batch.lastMovementStatus ? formatPurchaseTraceStatus(batch.lastMovementStatus) : 'Origen registrado'}
                                                </p>
                                                <p className="mt-1 text-xs text-slate-500">
                                                    {batch.closedAt ? `Cerrada ${formatProductHistoryDate(batch.closedAt)}` : 'Sin cierre final registrado en la línea'}
                                                </p>
                                            </div>
                                            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Costo unitario</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-800">{formatProductHistoryMoney(batch.unitCost)}</p>
                                                <p className="mt-1 text-xs text-slate-500">Origen económico de esta partida</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.9fr)]">
                        <div className="rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-sm">
                            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
                                <div>
                                    <p className={`text-xs font-semibold uppercase tracking-[0.22em] ${isTraceTab ? 'text-cyan-600' : 'text-slate-500'}`}>
                                        {isTraceTab ? 'Recorrido operativo' : 'Cronología completa'}
                                    </p>
                                    <h4 className="mt-1 text-xl font-bold text-slate-900">
                                        {isTraceTab ? 'Trazabilidad del producto' : 'Historial completo del producto'}
                                    </h4>
                                    <p className="mt-1 text-sm text-slate-500">
                                        {isTraceTab
                                            ? 'Acá se ve cada ciclo operativo completo del producto: compra, tránsito, ingreso, salida y entrega, con su estado de stock al cierre de cada tramo.'
                                            : 'Acá ves el resumen cronológico de cada ciclo del producto, agrupado por compra, salida o evento especial.'}
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center justify-end gap-2">
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                                        {annotatedMovementsHistory.length} eventos
                                    </span>
                                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                                        {timelineSummary.inboundCount} entradas
                                    </span>
                                    <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                                        {timelineSummary.outboundCount} salidas
                                    </span>
                                    <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                                        {timelineSummary.specialCount} especiales
                                    </span>
                                </div>
                            </div>
                            <div className="mt-4 space-y-4">
                                {loading ? (
                                    <p className="py-8 text-center text-sm text-slate-500">Cargando trazabilidad...</p>
                                ) : movementCycleGroups.length === 0 ? (
                                    <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                                        Este producto todavía no tiene movimientos operativos registrados.
                                    </p>
                                ) : (
                                    movementCycleGroups.map((group) => (
                                        <div key={group.id} className={`overflow-hidden rounded-[26px] border ${group.tone.shell}`}>
                                            <div className="flex gap-0">
                                                <div className={`w-1.5 shrink-0 ${group.tone.accent}`} />
                                                <div className="min-w-0 flex-1 p-4">
                                                    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/70 pb-3">
                                                        <div className="min-w-0">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${group.tone.badge}`}>
                                                                    {group.cycleTitle}
                                                                </span>
                                                                {group.first?.routeCodeSnapshot ? (
                                                                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                                                        Ruta {group.first.routeCodeSnapshot}
                                                                    </span>
                                                                ) : null}
                                                                {group.first?.referenceCodeSnapshot ? (
                                                                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                                                        Ref. {group.first.referenceCodeSnapshot}
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            <div className="mt-3 flex flex-wrap items-center gap-3">
                                                                <div>
                                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                                        Momento del ciclo
                                                                    </p>
                                                                    <p className={`mt-1 text-sm font-bold ${group.tone.title}`}>
                                                                        {group.rangeLabel}
                                                                    </p>
                                                                </div>
                                                                <div className={`rounded-2xl border px-3 py-2 ${group.tone.stockChip}`}>
                                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70">
                                                                        Stock al cierre
                                                                    </p>
                                                                    <p className="mt-1 text-sm font-bold">
                                                                        {group.closedStateLabel}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            {group.integrity ? (
                                                                <div className={`mt-3 inline-flex max-w-3xl flex-col rounded-2xl border px-3 py-2 ${CYCLE_INTEGRITY_TONE[group.integrity.tone] || CYCLE_INTEGRITY_TONE.info}`}>
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-75">
                                                                            Balance del ciclo
                                                                        </span>
                                                                        <span className="text-xs font-bold">
                                                                            {group.integrity.label}
                                                                        </span>
                                                                    </div>
                                                                    <p className="mt-1 text-xs leading-5 opacity-90">
                                                                        {group.integrity.detail}
                                                                    </p>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                        <div className="text-right text-xs text-slate-500">
                                                            <p>{group.items.length} evento{group.items.length === 1 ? '' : 's'}</p>
                                                        </div>
                                                    </div>

                                                    {isTraceTab ? (
                                                        <div className="mt-4 space-y-3">
                                                            {group.items.map((movement) => {
                                                                const movementMeta = movement.resolvedMovementMeta || PRODUCT_MOVEMENT_META[movement.movementType] || {
                                                                    label: String(movement.movementType || 'Movimiento').replace(/_/g, ' '),
                                                                    tone: 'border-slate-200 bg-slate-50 text-slate-700',
                                                                };
                                                                const routeData = movement.routeId ? routesById[movement.routeId] : null;
                                                                const transportLabel = movement.transportNameSnapshot || routeData?.transportName || routeData?.vehicleLabel || routeData?.transporterName || '';
                                                                const driverLabel = movement.driverNameSnapshot || [routeData?.driverName, routeData?.driverLastName].filter(Boolean).join(' ').trim();
                                                                const driverPhotoURL =
                                                                    movement.driverPhotoURLSnapshot ||
                                                                    routeData?.driverPhotoURL ||
                                                                    routeData?.currentDriverPhotoURL ||
                                                                    routeData?.photoURL ||
                                                                    '';
                                                                const operationReasonLabel = formatOperationReason(movement.operationReason || movement.operationType);
                                                                return (
                                                                    <div key={movement.id} className="rounded-[22px] border border-white/80 bg-white/85 p-4">
                                                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                                                            <div className="min-w-0">
                                                                                <div className="flex flex-wrap items-center gap-2">
                                                                                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${movementMeta.tone}`}>
                                                                                        {movementMeta.label}
                                                                                    </span>
                                                                                    {operationReasonLabel ? (
                                                                                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                                                                            {operationReasonLabel}
                                                                                        </span>
                                                                                    ) : null}
                                                                                </div>
                                                                                <p className="mt-2 text-sm font-semibold text-slate-900">
                                                                                    {movement.resolvedQuantityLabel} · {formatProductHistoryDate(movement.appliedAt || movement.createdAt)}
                                                                                </p>
                                                                                <p className="mt-1 text-xs font-medium text-slate-500">
                                                                                    Estado luego del evento: {movement.resolvedBucketsAfterLabel}
                                                                                </p>
                                                                                {movement.note ? <p className="mt-1 text-sm text-slate-600">{movement.note}</p> : null}
                                                                            </div>
                                                                            <div className="text-right text-xs text-slate-500">
                                                                                {movement.routeCodeSnapshot ? <p>Ruta {movement.routeCodeSnapshot}</p> : null}
                                                                                {movement.referenceCodeSnapshot ? <p>Ref. {movement.referenceCodeSnapshot}</p> : null}
                                                                            </div>
                                                                        </div>

                                                                        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                                                            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                                                                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Destino / contacto</p>
                                                                                <p className="mt-1 text-sm font-semibold text-slate-800">{movement.destinationNameSnapshot || 'Sin destino'}</p>
                                                                                <p className="mt-1 text-xs text-slate-500">{movement.customerNameSnapshot || 'Sin contacto asociado'}</p>
                                                                            </div>
                                                                            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                                                                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Transporte / chofer</p>
                                                                                <div className="mt-2 flex items-center gap-3">
                                                                                    <TracePersonAvatar
                                                                                        photoURL={driverPhotoURL}
                                                                                        displayName={driverLabel || 'Sin chofer vinculado'}
                                                                                        tone="violet"
                                                                                    />
                                                                                    <div className="min-w-0">
                                                                                        <p className="truncate text-sm font-semibold text-slate-800">{transportLabel || 'Sin transporte vinculado'}</p>
                                                                                        <p className="truncate text-xs text-slate-500">{driverLabel || 'Sin chofer vinculado'}</p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                                                                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Costo / venta</p>
                                                                                <p className="mt-1 text-sm font-semibold text-slate-800">
                                                                                    Compra {formatProductHistoryMoney(movement.unitCostSnapshot || movement.unitCost)}
                                                                                </p>
                                                                                <p className="mt-1 text-xs text-slate-500">
                                                                                    Venta {formatProductHistoryMoney(movement.saleUnitSnapshot)} · Margen {formatProductHistoryMoney(movement.marginSnapshot)}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="mt-4 space-y-2">
                                                            {group.items.map((movement) => {
                                                                const movementMeta = movement.resolvedMovementMeta || PRODUCT_MOVEMENT_META[movement.movementType] || {
                                                                    label: String(movement.movementType || 'Movimiento').replace(/_/g, ' '),
                                                                    tone: 'border-slate-200 bg-slate-50 text-slate-700',
                                                                };
                                                                return (
                                                                    <div key={movement.id} className="rounded-2xl border border-white/80 bg-white/85 px-3 py-3">
                                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                                            <div className="min-w-0">
                                                                                <p className="text-sm font-semibold text-slate-900">
                                                                                    {movementMeta.label} · {movement.resolvedQuantityLabel}
                                                                                </p>
                                                                                <p className="mt-1 text-xs text-slate-500">
                                                                                    {formatProductHistoryDate(movement.appliedAt || movement.createdAt)} · {movement.resolvedBucketsAfterLabel}
                                                                                </p>
                                                                            </div>
                                                                            <div className="text-right text-xs text-slate-500">
                                                                                <p>{movement.destinationNameSnapshot || movement.customerNameSnapshot || 'Sin destino'}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-sm">
                            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                        {isTraceTab ? 'Controles físicos' : 'Controles y diferencias'}
                                    </p>
                                    <h4 className="mt-1 text-xl font-bold text-slate-900">
                                        {isTraceTab ? 'Cotejo y auditoría' : 'Validaciones físicas del historial'}
                                    </h4>
                                </div>
                                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                                    {enrichedControlsHistory.length} controles
                                </span>
                            </div>
                            <div className="mt-4 space-y-3">
                                {loading ? (
                                    <p className="py-8 text-center text-sm text-slate-500">Cargando controles...</p>
                                ) : enrichedControlsHistory.length === 0 ? (
                                    <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                                        No hay controles físicos registrados para este producto.
                                    </p>
                                ) : (
                                    enrichedControlsHistory.map((control) => {
                                        let detailsCount = 0;
                                        let differencesCount = 0;
                                        try {
                                            const parsed = JSON.parse(control.countDetails || '[]');
                                            detailsCount = Array.isArray(parsed) ? parsed.length : 0;
                                            differencesCount = Array.isArray(parsed) ? parsed.filter((item) => Number(item?.difference || 0) !== 0).length : 0;
                                        } catch {
                                            detailsCount = 0;
                                            differencesCount = 0;
                                        }
                                        return (
                                            <div key={control.id} className="rounded-[24px] border border-slate-200 bg-slate-50/70 p-4">
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-semibold text-slate-900">
                                                            {String(control?.type || '').trim() === 'stock_adjustment'
                                                                ? `Ajuste del ${formatProductHistoryDate(control.endTime || control.createdAt)}`
                                                                : `Control del ${formatProductHistoryDate(control.endTime || control.createdAt)}`}
                                                        </p>
                                                        <p className="mt-1 text-xs text-slate-500">
                                                            {String(control?.type || '').trim() === 'stock_adjustment'
                                                                ? `Ajuste asentado · ${detailsCount} renglón${detailsCount === 1 ? '' : 'es'} · ${differencesCount} diferencia${differencesCount === 1 ? '' : 's'}`
                                                                : `${control.type === 'manual' ? 'Carga manual' : 'Carga desde archivo'} · ${detailsCount} renglón${detailsCount === 1 ? '' : 'es'} · ${differencesCount} diferencia${differencesCount === 1 ? '' : 's'}`}
                                                        </p>
                                                        {String(control?.type || '').trim() === 'stock_adjustment' && control.adjustmentReasonSnapshot ? (
                                                            <p className="mt-1 text-xs font-medium text-amber-700">
                                                                {control.adjustmentReasonSnapshot}
                                                            </p>
                                                        ) : null}
                                                        <div className="mt-3 flex items-center gap-3">
                                                            <ControlResponsibleAvatar
                                                                photoURL={control.resolvedControllerPhotoURL}
                                                                displayName={control.resolvedControllerDisplayName}
                                                            />
                                                            <div className="min-w-0">
                                                                <p className="truncate text-sm font-semibold text-slate-800">
                                                                    {control.resolvedControllerDisplayName}
                                                                </p>
                                                                <p className="truncate text-xs text-slate-500">
                                                                    {control.sheetTitleSnapshot ? `Planilla ${control.sheetTitleSnapshot}` : 'Control físico registrado'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => setExpandedControlId((prev) => prev === control.id ? null : control.id)} className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100">
                                                        {expandedControlId === control.id ? 'Ocultar detalle' : 'Ver detalle'}
                                                    </button>
                                                </div>
                                                {expandedControlId === control.id ? (
                                                    <ControlDetailView control={{ ...control, productName: product.name }} showMessage={showMessage} />
                                                ) : null}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ProductObservationHistoryModal = ({ show, onClose, productName, onTagSelected, showMessage, headerTitle, db, appId, companyId = 'default' }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show || !productName) return;

        const fetchHistory = async () => {
            setLoading(true);
            setError(null);
            try {
                const companyProductsRef = collection(db, resolveStockProductsCollectionPath(companyId));
                const legacyProductsRef = collection(db, legacyStockProductsCollectionPath(appId));
                let productSnapshot = await getDocs(query(companyProductsRef, where("name", "==", productName), limit(1)));
                let source = 'company';
                if (productSnapshot.empty) {
                    productSnapshot = await getDocs(query(legacyProductsRef, where("name", "==", productName), limit(1)));
                    source = 'legacy';
                }

                if (productSnapshot.empty) {
                    setError(`Producto "${productName}" no encontrado.`);
                    showMessage(`Producto "${productName}" no encontrado para historial de observación.`, "error");
                    setHistory([]);
                    return;
                }

                const productId = productSnapshot.docs[0].id;

                const historyRef = collection(
                    db,
                    source === 'legacy'
                        ? legacyStockProductControlsCollectionPath(appId, productId)
                        : resolveStockControlsCollectionPath(companyId)
                );
                const q = source === 'legacy'
                    ? query(historyRef, orderBy('endTime', 'desc'))
                    : query(historyRef, where('productId', '==', productId));
                const querySnapshot = await getDocs(q);
                const fetchedHistory = querySnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                        endTime: doc.data().endTime?.toDate()
                    }))
                    .sort((a, b) => {
                        const timeA = a?.endTime instanceof Date ? a.endTime.getTime() : 0;
                        const timeB = b?.endTime instanceof Date ? b.endTime.getTime() : 0;
                        return timeB - timeA;
                    });
                setHistory(fetchedHistory);
            } catch (err) {
                console.error("Error fetching product observation history:", err);
                setError("No se pudo cargar el historial de observaciones.");
                showMessage("Error al cargar el historial de observaciones del producto.", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [show, productName, showMessage, db, appId, companyId]);

    const handleSelectTag = (entry) => {
        const date = entry.endTime ? entry.endTime.toLocaleDateString('es-AR') : 'N/A';
        let finalCount = 'N/A';
        let difference = 'N/A';
        let observation = 'Sin observación';

        try {
            const details = JSON.parse(entry.countDetails);
            if (details && details.length > 0) {
                const lastItem = details[details.length - 1];
                finalCount = lastItem.stockPhysical !== undefined ? lastItem.stockPhysical : 'N/A';
                difference = lastItem.difference !== undefined ? lastItem.difference : 'N/A';
                observation = lastItem.observation || observation;
            }
        } catch (e) {
            console.error("Error parsing countDetails for tag:", e);
        }
        
        const tag = `[Control ${date} | Final: ${finalCount} | Dif: ${difference} | Obs: ${observation}]`;
        onTagSelected(tag);
        onClose();
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4 pb-4 border-b">
                    <h3 className="text-2xl font-bold text-stone-900">{headerTitle || `Historial de Observaciones: ${productName}`}</h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-200">
                        <Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6"/>
                    </button>
                </div>
                <div className="flex-grow overflow-y-auto pr-2">
                    {loading && <p className="text-center text-stone-500">Cargando historial...</p>}
                    {error && <p className="text-center text-red-500">{error}</p>}
                    {!loading && history.length === 0 && <p className="text-center text-stone-500">No hay historial de observaciones para este producto.</p>}
                    {!loading && history.length > 0 && (
                        <ul className="space-y-4">
                            {history.map((entry) => {
                                let finalCountDisplay = 'N/A';
                                let differenceDisplay = 'N/A';
                                let observationDisplay = entry.observation || 'Sin observación';
                                try {
                                    const details = JSON.parse(entry.countDetails);
                                    if (details && details.length > 0) {
                                        const lastItem = details[details.length - 1];
                                        finalCountDisplay = lastItem.stockPhysical !== undefined ? lastItem.stockPhysical : 'N/A';
                                        differenceDisplay = lastItem.difference !== undefined ? lastItem.difference : 'N/A';
                                        if (lastItem.observation) observationDisplay = lastItem.observation;
                                    }
                                } catch (e) {
                                    console.error("Error parsing countDetails for display:", e);
                                }

                                return (
                                <li key={entry.id} className="p-4 border border-stone-200 rounded-lg shadow-sm bg-stone-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div className="flex-grow">
                                        <div className="text-sm font-semibold text-stone-600 mb-1">
                                            Fecha: {entry.endTime ? entry.endTime.toLocaleDateString('es-AR') : 'N/A'}
                                        </div>
                                        <p className="text-stone-700 text-base">
                                            Conteo Final: <span className="font-bold">{finalCountDisplay}</span>
                                            <span className={`ml-2 font-bold ${differenceDisplay !== 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                (Dif: {differenceDisplay})
                                            </span>
                                        </p>
                                        {observationDisplay && (
                                            <p className="text-stone-700 text-sm mt-1">
                                                <span className="font-semibold">Obs:</span> {observationDisplay}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleSelectTag(entry)}
                                        className="bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors flex-shrink-0 mt-2 sm:mt-0"
                                    >
                                        Etiquetar
                                    </button>
                                </li>
                            )})}
                        </ul>
                    )}
                </div>
                <div className="flex justify-end mt-4 pt-4 border-t">
                    <button onClick={onClose} className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
